import { CdkDrag } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef, Component, ElementRef, NgZone, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { isCommunityUrl } from 'src/app/core/utils/community-route.util';
import { sortGroupsAlphabetically } from 'src/app/core/utils/sort-groups.util';
import { EventQuickRegisterService } from 'src/app/core/services/event-quick-register.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { filter, Subscription, BehaviorSubject } from 'rxjs';
import { API, CONST_AWS_API, DEFAULT_IMAGE } from 'src/app/core/constants';
import { AffiliateData, AffiliateService } from 'src/app/core/services/affiliate.service';
import { AppService } from 'src/app/core/services/app.service';
import { CommanSubscribeService } from 'src/app/core/services/comman-subscribe.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { UserService } from 'src/app/core/services/user.service';
import { environment } from 'src/environments/environment';
import WaveSurfer from 'wavesurfer.js';
import MicrophonePlugin from 'wavesurfer.js/dist/plugin/wavesurfer.microphone.js';
import { a, API as AWS_API } from "aws-amplify";

import { ConfirmationDialogComponent } from '../../components/confirmation-dialog/confirmation-dialog.component';
import { ConsumerSignInComponent } from '../../components/landing-pages/consumer/consumer-sign-in/consumer-sign-in.component';
import { FanNewsStory, toFanNewsStories } from './fan-news/fan-news.model';
import { PAUSED_NOTE, canShowAgent, isPausedReply, serverEnforcementEnabled } from './ai-agent-credit';
import { AgentGroupsDialogComponent } from './agent-groups-dialog/agent-groups-dialog.component';
import { AskChip } from './agent-ask-bar/agent-ask-bar.component';
import {
  AgentScope, buildQuickActions, getAskContext, getMemberBarGroups, getRecommendedSubtitle, getWelcomeSubtitle,
  QuickAction, ReasonContext,
} from './agent-home.helpers';
import { collapseTableRows, hiddenTableRowCount } from './agent-table-rows.helpers';
declare let $: any;

const BADGE_COLORS: Record<string, string> = {
  LIVE: '#e53935', UPCOMING: '#f5a623', VOD: '#7b1fa2',
  'MERCH DROP': '#1565c0', COMMUNITY: '#2e7d32',
};

const REMIND_ME_LABEL = 'Remind Me';

@Component({
  selector: 'app-ai-agent',
  templateUrl: './ai-agent.component.html',
  styleUrls: ['./ai-agent.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AiAgentComponent {
  @ViewChild('messageInput') messageInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild('preferencesDialogTpl') preferencesDialogTpl!: TemplateRef<any>;
  private preferencesDialogRef: MatDialogRef<any> | null = null;
  @ViewChild('chatHistoryDialogTpl') chatHistoryDialogTpl!: TemplateRef<any>;
  private chatHistoryDialogRef: MatDialogRef<any> | null = null;
  // One-click "Remind Me" for free events (US-20) - v2 design only (AiAgentV2Component turns it on).
  protected remindMeEnabled = false;

  settingsData: any;
  org_info: any = {};
  originalChannelLogo: any;
  originalChannelBannerImage: any;
  originalChannelAgentAvatar: any;
  defaultImage: any = DEFAULT_IMAGE.IMAGE_3;

  assets_url = environment.assets_url;


  // --fa-bg/--fa-accent/--fa-text/--fa-form are set globally by CommanSubscribeService.agentStyle()
  // whenever webpage theme settings load; these getters just expose the resolved values for
  // template bindings that need the color directly (not via CSS var).
  get accentColor() { return this.settingsData?.ai_agent_accent_color || '#eba921'; }
  get textColor() { return this.settingsData?.ai_agent_text_color || '#f1eded'; }

  // Text rendered directly on an accentColor-filled surface (user chat bubbles, the
  // scroll-to-bottom arrow, selected preference chips/buttons) needs to contrast against
  // accentColor specifically - not just be "the theme's text color". textColor is chosen to
  // read well against the panel background (--fa-bg), which says nothing about whether it
  // will also be legible against accentColor, and accentColor's own brightness varies wildly
  // per org (e.g. our default bright yellow #eba921 vs. a client's dark red). Same
  // luminance-difference-threshold approach as getItemBadgeFontColor below: keep the
  // configured textColor when it already contrasts well against accentColor, otherwise
  // auto-flip to black/white based on accentColor's own luminance.
  get accentTextColor(): string {
    const bgLuminance = this.getLuminance(this.accentColor);
    const fontLuminance = this.getLuminance(this.textColor);
    if (Math.abs(bgLuminance - fontLuminance) < 0.35) {
      return bgLuminance < 0.5 ? '#ffffff' : '#000000';
    }
    return this.textColor;
  }

  // Carousel "UPCOMING EVENT / LIVE NOW" pill: text is accentColor, so the pill behind it
  // must contrast against accentColor rather than being a fixed grey. Dark accents
  // (green, red, navy) get a light pill without a text shadow; light accents keep a dark pill.
  get accentBadgeStyle(): { [key: string]: string } {
    const isDarkAccent = this.getLuminance(this.accentColor) < 0.5;
    return {
      'color': this.accentColor,
      'background': isDarkAccent ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.6)',
      'text-shadow': isDarkAccent ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.6)'
    };
  }

  // Carousel title/countdown/meta icons always sit on the fixed near-black image overlay
  // (.fa-cc-overlay), so a dark theme text or accent color would vanish there - swap to
  // white in that case. Exposed as CSS vars scoped to the carousel.
  get carouselColorVars(): { [key: string]: string } {
    return {
      '--fa-cc-text': this.isDarkColor(this.textColor) ? '#ffffff' : this.textColor,
      '--fa-cc-accent': this.isDarkColor(this.accentColor) ? '#ffffff' : this.accentColor
    };
  }

  // Carousel CTA: configured toastr bg/font pair, falling back to accentColor for the bg,
  // with the font auto-flipped to black/white when it wouldn't be legible on that bg.
  get carouselCtaStyle(): { [key: string]: string } {
    const bg = this.settingsData?.toastr_bg_color || this.accentColor;
    const font = this.settingsData?.toastr_font_color || '#000000';
    const bgLuminance = this.getLuminance(bg);
    const readable = Math.abs(bgLuminance - this.getLuminance(font)) < 0.35
      ? (bgLuminance < 0.5 ? '#ffffff' : '#000000')
      : font;
    return { 'background-color': bg, 'color': readable };
  }

  // Shown in the carousel's slot when there are no live/upcoming events - the same banner the
  // fan portal home page (ic-home) shows, not the agent-specific channel_agent_banner. On a group
  // page that's the group's own group_banner, same as ic-home's group switch.
  get homeBannerImage(): string {
    const group = this.currentGroupSlug && this.groupsData?.find((g: any) => g.group_slug === this.currentGroupSlug);
    return group?.group_banner || this.org_info?.channel_banner || this.settingsData?.banner_image || '';
  }

  // Fan's paid plans (subscribePayedInfoData) and this network's plan catalogue
  // (performer-info plans; group plans come from groupsData[].plans).
  subscribedPlans: any[] = [];
  channelPlans: any[] = [];

  // Same rule as ic-home.checkIsSubscribed, as a getter so it follows group switches: on a group
  // with its own plan set (separate_subscription_plan, or a channel with its own plan) only that
  // group's plans count; otherwise only network-level plans (network_group_id 0) count.
  get isSubscribed(): boolean {
    if (!this.subscribedPlans?.length) return false;
    const channelInfo = this.settingsData?.channelInfo;
    const cid = channelInfo?.channel_id;
    const groupId = Number(this.currentGroupId) || 0;
    const activeGroup = groupId > 0 ? (this.groupsData || []).find((g: any) => Number(g.id) === groupId) : null;
    const channelHasOwnPlan = !!(channelInfo && channelInfo.subscription == 1 && cid);
    const groupHasSeparatePlan = !!(activeGroup && (activeGroup.separate_subscription_plan == 1 || channelHasOwnPlan));
    const catalogue = [...(this.channelPlans || []), ...(this.groupsData || []).flatMap((g: any) => g.plans || [])];
    const planGroupId = (planId: any) => Number(catalogue.find((p: any) => p.id == planId)?.network_group_id) || 0;
    return this.subscribedPlans.some((p: any) =>
      p?.payment === 'success'
      && (!p.channel_id || (cid && p.channel_id == cid))
      && (groupHasSeparatePlan ? planGroupId(p.plan_id) === groupId : planGroupId(p.plan_id) === 0));
  }

  userInfo: any = {};

  channelName = '';
  org_id = '';
  organizationKey = '';
  private affiliateData: AffiliateData = { orgResults: [], fanPrograms: [] };
  private affiliateLoadedFor = '';
  channelId = '';
  performerId = '';
  channelDisplayName = '';
  businessName = '';

  // Dashboard data
  isDashboardLoading = true;
  fanName = '';
  networkName = '';
  bannerImage = '';
  recommendedItems: any[] = [];
  // Single-source cards for the "Recommended For You" grid - populated from
  // AI_RECOMMENDED_FOR_YOU (fetchRecommendedForYou), one per category.
  private aiRecommendedEvent: any = null;
  private aiRecommendedContent: any = null;
  private aiRecommendedMerch: any = null;
  private aiRecommendedCommunity: any = null;
  // uReport news for the Fan Hub "Latest News" card - `ureport_news` from the same
  // AI_RECOMMENDED_FOR_YOU response, already filtered by network/group on the backend.
  ureportNews: FanNewsStory[] = [];
  readonly navItems = [
    { icon: 'home', label: 'Home', route: '', requiresAuth: false, guestOnly: false },
    { icon: 'account_circle', label: 'Profile', route: '/user-dashboard/profile-settings/basic-info', requiresAuth: true, guestOnly: false },
    { icon: 'handshake', label: 'Become an Affiliate', route: null, requiresAuth: true, guestOnly: false, isAffiliate: true },
    { icon: 'favorite', label: 'Wish List', route: '/user-dashboard/My_WishList', requiresAuth: true, guestOnly: false },
    { icon: 'event', label: 'Events', route: '/user-dashboard/My-events', requiresAuth: true, guestOnly: false },
    { icon: 'credit_card', label: 'Payments', route: '/user-dashboard/payment-methods', requiresAuth: true, guestOnly: false },
    { icon: 'shopping_bag', label: 'Purchases', route: '/user-dashboard/my-purchases', requiresAuth: true, guestOnly: false },
    { icon: 'receipt_long', label: 'Order History', route: '/user-dashboard/order-history', requiresAuth: true, guestOnly: false },
    { icon: 'volunteer_activism', label: 'Tips/Donations', route: '/user-dashboard/tips-donations', requiresAuth: true, guestOnly: false },
    { icon: 'login', label: 'Login', route: null, requiresAuth: false, guestOnly: true },
    { icon: 'logout', label: 'Logout', route: null, requiresAuth: true, guestOnly: false, isLogout: true },
  ];

  get visibleNavItems() {
    return this.navItems.filter(item => {
      if ((item as any).isAffiliate) return this.showAffiliateItem;
      if (item.requiresAuth) return !!this.userInfo?.id;
      if (item.guestOnly) return !this.userInfo?.id;
      return true;
    });
  }

  isNavItemActive(item: any): boolean {
    if (!item.route && item.route !== '') return false;
    const currentPath = this.router.url.split('?')[0];
    if (item.route === '') return currentPath === `/${this.channelName}`;
    return currentPath.includes(item.route);
  }

  onNavItemClick(item: any): void {
    if (item.guestOnly) { this.openSignIn(); return; }
    if ((item as any).isLogout) { this.signout(); return; }
    if ((item as any).isAffiliate) { this.openAffiliateLink(); return; }
    this.navigate(`/${this.channelName}${item.route}`);
  }

  getNavLabel(item: any): string {
    if (item.isAffiliate && this.affiliateFlags.isAffiliate) return 'Sign in as Affiliate';
    return item.label;
  }

  // Group from the current URL (/<channel>/<groupSlug>) - network context when none matches
  private get affiliateGroup(): any {
    const slug = this.router.url.split('?')[0].split('/').filter(Boolean)[1];
    return slug ? this.groupsData?.find((g: any) => g.group_slug === slug) || null : null;
  }

  private get affiliateFlags() {
    return this.affiliateService.evaluate(this.affiliateData.orgResults, this.organizationKey, this.affiliateGroup);
  }

  get showAffiliateItem(): boolean {
    if (!this.userInfo?.id || !this.organizationKey) return false;
    return this.affiliateService.isVisible(this.affiliateFlags, this.affiliateData.fanPrograms);
  }

  private loadAffiliateData(): void {
    const orgId = this.commanSubscribeService.perfomerInfoData?.data?.Data?.[0]?.organization_id || this.org_id;
    const email = this.userInfo?.email;
    if (!email || !orgId || !this.organizationKey) return;
    const cacheKey = `${email}|${orgId}`;
    if (this.affiliateLoadedFor === cacheKey) return;
    this.affiliateLoadedFor = cacheKey;
    this.affiliateService.loadData(email, orgId).subscribe(data => this.affiliateData = data);
  }

  private resetAffiliateData(): void {
    this.affiliateData = { orgResults: [], fanPrograms: [] };
    this.affiliateLoadedFor = '';
  }

  openAffiliateLink(): void {
    const performer = this.commanSubscribeService.perfomerInfoData?.data?.Data?.[0];
    const organizationId = performer?.organization_id || this.org_id;
    const fullName = `${this.userInfo?.user_first_name} ${this.userInfo?.user_last_name}`;
    (window as any).umami?.track(`Become an Affiliate clicked on AI Agent - ${fullName}`);
    this.commanSubscribeService.sendTriggeredEvent({
      actionType: 'ai-agent',
      metaData: { title: 'Become an Affiliate clicked on AI Agent', timeStamp: new Date().toISOString() },
      organizationId: organizationId || null,
      clickAction: 'ai-agent',
    });
    const url = this.affiliateService.buildUrl({
      organizationKey: this.organizationKey,
      organizationId,
      organizationName: this.org_info?.organization_name || this.org_info?.performer_display_name || this.networkName || '',
      affiliateKey: this.affiliateFlags.affiliateKey,
      userInfo: this.userInfo,
      activeGroup: this.affiliateGroup,
    });
    window.open(url, '_blank');
  }

  // Home screen copy and lists (specs/ai-agent-home-redesign). Array/object values are memoized
  // on their inputs so child @Inputs keep a stable reference between change-detection runs.
  private readonly memo = new Map<string, { deps: any[]; value: any }>();

  private memoize<T>(name: string, deps: any[], build: () => T): T {
    const hit = this.memo.get(name);
    if (hit && hit.deps.length === deps.length && hit.deps.every((d, i) => d === deps[i])) return hit.value;
    const value = build();
    this.memo.set(name, { deps, value });
    return value;
  }

  get agentScope(): AgentScope {
    return { networkName: this.networkName, groupsLabel: this.groupsLabel, groupName: this.currentGroupSlug ? (this.currentGroupName || '').trim() : '' };
  }

  get welcomeSubtitle(): string { return getWelcomeSubtitle(this.agentScope); }

  get askContext(): string { return getAskContext(this.agentScope); }

  get savedPreferenceNames(): string[] {
    return this.memoize('savedPreferenceNames', [this.savedGroupItems, this.groupProfileSaved],
      () => this.groupProfileSaved ? this.savedGroupItems.filter(i => i.is_active !== false).map(i => i.name).filter(Boolean) : []);
  }

  get recommendedSubtitle(): string { return getRecommendedSubtitle(this.agentScope, this.savedPreferenceNames); }

  get homeQuickActions(): QuickAction[] {
    return this.memoize('quickActions', [this.networkName, this.groupsLabel], () => buildQuickActions(this.agentScope));
  }

  // v2 group lists (top-bar dropdown, member bar, dialog) show A-Z; groupsData keeps API order
  // for the plan/theme/slug lookups.
  get sortedGroups(): any[] {
    return this.memoize('sortedGroups', [this.groupsData], () => sortGroupsAlphabetically(this.groupsData));
  }

  get memberBarGroups(): any[] {
    return this.memoize('memberBarGroups', [this.sortedGroups, this.currentGroupSlug],
      () => getMemberBarGroups(this.sortedGroups, this.currentGroupSlug));
  }

  get reasonContext(): ReasonContext {
    return this.memoize('reasonContext',
      [this.groupsData, this.savedGroupNetworkIds, this.savedPreferenceNames, this.currentGroupSlug, this.currentGroupName],
      () => ({
        groups: this.groupsData || [],
        savedGroupIds: this.savedGroupNetworkIds || [],
        savedPreferenceNames: this.savedPreferenceNames,
        currentGroupSlug: this.currentGroupSlug,
        currentGroupName: (this.currentGroupName || '').trim(),
      }));
  }

  // Bound so the recommended child can call them without losing `this`.
  readonly badgeColorFn = (item: any): string => this.getItemBadgeColor(item);
  readonly badgeFontColorFn = (item: any): string => this.getItemBadgeFontColor(item);

  // Static investment cards injected per channel
  private readonly CHANNEL_STATIC_EVENTS: Record<string, any[]> = {
    gfl: [{
      title: 'OWN THE FUTURE OF TEAM MMA',
      description: 'Invest in Global Fight League through Dealmaker and join thousands of fans shaping the future of team MMA. Your support fuels the league, the teams, and the next era of fan ownership.',
      teamLogo: '',          // replace with actual GFL arena image URL
      date: 'Ends Jun 26, 2025',
      time: '',
      location: '',
      days: '28', hrs: '00', min: '00', sec: '00',
      isLive: false,
      isInvestment: true,
      ctaLabel: 'Invest Now',
      ctaIcon: 'trending_up',
      ctaUrl: 'https://www.dealmaker.tech/gfl',
      slug: '',
      channelName: 'gfl',
      groupSlug: '',
      eventDate: new Date('2025-06-26T23:59:59'),
      investStats: {
        raised: '$8.7M USD',
        investors: '2,846',
        daysLeft: '28',
        minInvestment: '$100 USD',
      }
    }]
  };

  // Fan Hub
  nextHubEvents: any[] = [];
  liveHubStream: any = null;
  exclusiveHubOffer: any = null;
  savedGroupNetworkIds: number[] = [];
  private countdownInterval: any;
  // Center event carousel
  centerCarouselIndex = 0;
  // Chat
  messagesList: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  chatHistories: any[] = [];
  // Hub shows the most recent chats (server sorts newest first); "Show all" expands in place
  readonly CHAT_PREVIEW_COUNT = 5;
  showAllChats = false;
  currentChatId: string | null = null;
  isLoadingHistory = false;
  threadId: string | null = null;
  userInput = '';
  isLoading = false;
  loadingStep = 0;
  private intervalId: any;

  // Streaming
  currentStreamingMessage: any = null;
  streamingSubscription: Subscription | null = null;
  isMessageStreaming = false;
  private hasReplyStarted = false;
  private lastUserInput = '';
  private orgInfoLoaded = false;

  // Initial greeting
  isFirstBotResponse = true;
  private initialMessageSent = false;

  // Preferences & personalization
  hasPreferred = false;
  hasPreferredChannel = false;
  preferredObj: any;
  private preferencesCheckInFlight = false;
  private personalizeMessageDisplayed = false;

  // Sub-channels
  subChannels: any;
  selectedChannelData: any;
  selectedSubChannels: any[] = [];
  // Snapshot of the saved selection taken when entering edit mode, so cancelling
  // (the X button, or minimizing mid-edit) can restore exactly what was there before.
  private savedSelectedSubChannels: any[] = [];
  isSavingChannelProfile = false;
  channelProfileSaved = false;
  isEditingChannelProfile = false;
  savedPreferredChannelIds: number[] = [];
  savedPreferredChannelNames: string[] = [];

  // Grouping questions
  groupingQuestions: any[] = [];
  allPreferenceQuestions: any[] = [];
  grouptypeSelectionQuestion: any = null;
  groupInfo: any = { total_questions: 0, status: 'pending' };
  private groupTypeDisplayed = false;

  // Merch tag options are ranked/fairly-merged server-side across whichever groups are
  // currently selected (see ai-agents-fan-service's fair_merge_top_tags) - not a static
  // membership filter like category/event_type/genre, so they're refreshed via a debounced
  // re-fetch instead of filterOptionsByCurrentGroup. See refreshMerchTagOptionsDebounced.
  private merchTagRefreshTimer: any = null;
  private merchTagRefreshToken = 0;
  // Section-level loading indicator for the merch_tag block (template-bound) - true only while
  // the actual HTTP request is in flight, not during the debounce wait. Never the global loader.
  merchTagOptionsLoading = false;

  // Group context (for sub-group pages)
  currentGroupId: any = 0;
  currentGroupName = '';
  currentGroupSlug = '';
  currentMatchedGroup: any = null;
  groupsData: any;
  // Groups row section title - org-configurable (e.g. "Teams", "Conferences"), same source
  // field ic-home.component.ts uses for its own groups switcher heading.
  groupsLabel = 'Group';

  // Dynamic Agent Name (specs/dynamic-agent-name) - partner-configured fan-facing
  // agent name (e.g. "Freddy"), set in Network Settings, read from the
  // getPerformerInfo response's flat `agent_name` field (mirrors how
  // groupsLabel reads network_type_label above). Network-level only - this is
  // deliberately never re-read per group, so it stays constant across group
  // switches even though the logo/banner/theme do switch. Empty string means
  // "no custom name set" - templates fall back to the existing default label.
  displayAgentName = '';

  // Group support flag (from performer info)
  isGroupSupported = false;

  // Input control
  disableSend = false;

  // Credits
  availableCredits: number | null = null;

  // Scroll arrow
  showScrollToBottomArrow = false;
  private readonly scrollThreshold = 100;
  private isExpandingTable = false;
  private lastScrollTime = 0;
  private readonly scrollThrottleDelay = 200;

  // Table expansion
  expandedMessages: Set<string> = new Set();
  readonly MAX_TABLE_ROWS = 5;

  // Previous design (app-ai-agent, environment.enableNewAgentHome = false) quick actions and chips.
  readonly quickActions = [
    { icon: 'event', label: 'Find Events', sublabel: 'Near You', route: '/events', isLive: false, message: 'What events are coming up?' },
    { icon: 'live_tv', label: 'Watch', sublabel: 'Live Games', route: '/live', isLive: true, message: "What's streaming live right now?" },
    { icon: 'shopping_cart', label: 'Shop', sublabel: 'Merch & Drops', route: '/network-market', isLive: false, message: 'Browse Merchandise' },
    { icon: 'star_border', label: 'Follow', sublabel: 'Teams & Players', route: '/teams', isLive: false, message: 'Show teams and players to follow' },
    { icon: 'group', label: 'Join', sublabel: 'Communities', route: '/communities', isLive: false, message: 'Find communities to join' },
  ];
  readonly suggestions = ['What games are on today?', 'Show me merch drops', 'Find events near me'];

  readonly askChips: AskChip[] = [
    { label: 'Find upcoming games', message: 'Which games are coming up?' },
    { label: 'See my highlights', message: 'Show highlights from my teams' },
    { label: 'Show team merch', message: 'Find merchandise from teams I follow' },
    { label: 'Find communities', message: 'Find fan communities' },
  ];

  // File attachments
  selectedFiles: any[] = [];

  // Voice recording
  private mediaRecorder!: MediaRecorder;
  private audioChunks: Blob[] = [];
  audioUrl: string | null = null;
  audioBlob: Blob | null = null;
  waveSurferLive: WaveSurfer | null = null;
  waveSurferFrozen: WaveSurfer | null = null;
  isRecording = false;
  isProcessing = false;
  private isCancelled = false;
  private pendingSend = false;

  // Popup state
  isExpanded = false;
  private skipAutoExpand = false;
  canShowAgent = false;
  // Set by a paused reply from the Fan Agent (channel owner's AI credits paused); the message box is disabled for this visit.
  chatPaused = false;
  readonly pausedNote = PAUSED_NOTE;
  isAgentEnabled = false;
  orgCreditsInFlightFor: string = null;
  isBigbeeHomePage = false;

  // Community pages have a message composer at the bottom; the collapsed bubble is lifted above it.
  get isCommunityPage(): boolean {
    return isCommunityUrl(this.router.url);
  }
  clientDetails: any = environment.clientDetails;

  // Grace period after org plan expiry (mirrors partner-ui's ai-agent grace-period gating)
  gracePeriodDays = 15;
  expiryDate: any;
  isPlanExpired = false;

  get daysToExpiry(): number {
    if (!this.expiryDate) return Infinity;
    const expiry = new Date(this.expiryDate);
    const today = new Date();
    return Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  }

  get isInGracePeriod(): boolean {
    if (!this.isPlanExpired) return false;
    return this.gracePeriodDays + this.daysToExpiry > 0;
  }

  private subs = new Subscription();
  private orgDetailsFetched = false;
  private performerOrgDetailsInFlight = false;
  private dashboardCallId = 0;
  private lastRecommendedForYouResponse: any = null;
  // Group-option checkboxes are multi-select, so several groups can be checked at once with
  // no inherent order - tracks check order so the most-recently-checked group can win as
  // network_group_id, oldest first / most recent last.
  private groupOptionCheckOrder: number[] = [];
  // Same purpose as groupOptionCheckOrder, but for the Hero group-preferences panel
  // (toggleHeroPreference/saveHeroGroupingPreferences) - a separate selection UI.
  private lastToggledGroupOptionId: number | null = null;

  constructor(
    private commanSubscribeService: CommanSubscribeService,
    private appService: AppService,
    private affiliateService: AffiliateService,
    private storageService: StorageService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    public dialog: MatDialog,
    private toastr: ToastrService,
    private quickRegister: EventQuickRegisterService,
    private loader: NgxUiLoaderService,
  ) {
    this.settingsData = this.commanSubscribeService.icWebpageSettingsData;
  }

  ngOnInit(): void {
    const nonHomeRoutes = ['userhome', 'signup', 'communities', 'privacypolicy', 'cookiepolicy', 'termsandconditions', 'contactus', 'network-market'];

    // Parse channelName from URL — parent route param not reachable in lazy-loaded child
    const segments = this.router.url.split('?')[0].split('/').filter(Boolean);
    this.isBigbeeHomePage = this.router.url === '' || this.router.url === '/userhome' || this.router.url === '/' || nonHomeRoutes.includes(segments[0]);

    if (!this.channelName && segments.length > 0 && !nonHomeRoutes.includes(segments[0])) {
      this.channelName = segments[0];
    }

    // Router subscription to update isBigbeeHomePage and channelName on navigation
    this.subs.add(
      this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
        const url = this.router.url || '';
        const segs = url.split('?')[0].split('/').filter(Boolean);
        this.isBigbeeHomePage = url === '' || url === '/userhome' || url === '/' || nonHomeRoutes.includes(segs[0]);
        if (this.isBigbeeHomePage) {
          this.isExpanded = false;
        } else if (segs.length > 0) {
          const newChannel = segs[0];
          if (newChannel !== this.channelName) {
            this.channelName = newChannel;
            this.orgDetailsFetched = false;
            this.performerOrgDetailsInFlight = false;
          }
          if (this.userInfo?.id && !this.orgDetailsFetched) {
            this.getPerformerOrgDetails();
          }
        }

        // Update org_info logo/banner based on current group from URL
        const navUrlSegments = url.split('/');
        const navGroupSlug = navUrlSegments.length > 2 ? navUrlSegments[2] : null;
        const navMatchedGroup = this.groupsData?.find((g: any) => g.group_slug === navGroupSlug);
        const prevGroupId = this.currentGroupId;
        if (navMatchedGroup) {
          this.currentMatchedGroup = navMatchedGroup;
          this.currentGroupId = navMatchedGroup.group_id || navMatchedGroup.id;
          this.currentGroupName = navMatchedGroup.group_name || navMatchedGroup.name || '';
          this.currentGroupSlug = navMatchedGroup.group_slug || '';
          this.org_info = { ...this.org_info, channel_logo: navMatchedGroup.group_logo || this.originalChannelLogo, channel_banner_image: navMatchedGroup.group_agent_banner || this.originalChannelBannerImage };
        } else {
          this.currentMatchedGroup = null;
          this.currentGroupId = 0;
          this.currentGroupName = '';
          this.currentGroupSlug = '';
          if (this.originalChannelLogo) {
            this.org_info = { ...this.org_info, channel_logo: this.originalChannelLogo, channel_banner_image: this.originalChannelBannerImage };
          }
        }

        // Reload dashboard when group context changes (group→none, none→group, group→different group)
        if (this.currentGroupId !== prevGroupId && this.org_id) {
          // The widget only renders when the agent is enabled (canShowAgent), so skip its data calls otherwise.
          const agentEnabled = this.isAgentEnabled;
          if (agentEnabled) this.loadDashboard();

          // Reset chat so the previous group/network thread is not shown in the new context
          this.threadId = null;
          this.currentChatId = null;
          this.messagesList.next([]);
          this.initialMessageSent = false;
          this.isFirstBotResponse = true;
          this.chatHistories = [];
          if (agentEnabled && this.userInfo?.id) this.loadAllChatHistories();

          // group → network: stale groupInfo must be cleared and questions reloaded
          if (!this.currentGroupId && prevGroupId && this.userInfo?.id) {
            this.groupInfo = { total_questions: 0, status: 'pending' };
            if (agentEnabled) this.fetchGroupingQuestions();
          }
          // Re-fetch preferences for the new group context (entering OR leaving a group) -
          // otherwise the panel keeps showing whatever was cached from the previous context.
          if (agentEnabled && this.userInfo?.id) {
            this.checkAgentPreference();
            this.checkChannelAgentPreference();
          }
        }

        // Auto-expand agent when landing on channel home (e.g. after "Go to Network")
        const capturedUrl = url;
        setTimeout(() => {
          try {
            if (capturedUrl.endsWith('/signin')) return;
            const isChannelHomeOnly = !!this.channelName && capturedUrl.split('?')[0] === `/${this.channelName}`;
            if (!this.isBigbeeHomePage && this.canShowAgent && isChannelHomeOnly && this.isAgentEnabled && !this.skipAutoExpand) {
              if (this.threadId) {
                this.isFirstBotResponse = false;
                this.isExpanded = true;
                this.scrollToBottomImmediate();
              } else {
                this.isExpanded = true;
                this.initialMessageSent = false;
                this.messagesList.next([]);
                this.isFirstBotResponse = true;
                this.sendInitialGreeting();
              }
            }
          } catch (e) { }
        }, 1500);
      })
    );

    // ── Window globals for interactive HTML embedded in chat messages ──────────

    (window as any).saveSubchannelSelection = (selectedValue: any) => {
      if (!selectedValue) return;
      const radios = document.querySelectorAll('input[name="subchannel"]');
      radios.forEach((radio: any) => { radio.disabled = true; });
      const btn = document.querySelector('.save-preferences-btn') as HTMLButtonElement;
      if (btn) {
        btn.disabled = true;
        btn.innerText = 'Saved';
        btn.style.opacity = '0.6';
        btn.style.cursor = 'not-allowed';
      }
      this.disableSend = false;
      const selectedChannels = this.subChannels.filter((c: any) => c.channel_id == selectedValue);
      this.selectedChannelData = selectedChannels;
      const channelNames = this.selectedChannelData?.map((c: any) => c.channel_display_name).join(', ');
      this.userInput = `Selected Team${this.selectedChannelData.length > 1 ? 's are ' : ' is '} ${channelNames}`;
      this.sendMessage();
    };

    // Records check order for group-option checkboxes (multi-select) so the most recently
    // checked one can win as network_group_id when preferences are saved.
    (window as any).onGroupOptionToggle = (optionId: number, checked: boolean) => {
      this.groupOptionCheckOrder = this.groupOptionCheckOrder.filter(id => id !== optionId);
      if (checked) this.groupOptionCheckOrder.push(optionId);
    };

    (window as any).onGroupTypeChange = (containerId: string) => {
      const groupTypeCheckboxes = document.querySelectorAll(`#grouptype-opts-${containerId} input[type="checkbox"]:checked`);
      const divisionCheckboxes = document.querySelectorAll(`#division-opts-${containerId} input[type="checkbox"]:checked`);
      const selectedGroupTypes = Array.from(groupTypeCheckboxes).map((cb: any) => cb.value);
      const selectedDivisions = Array.from(divisionCheckboxes).map((cb: any) => parseInt(cb.value));
      const preferenceContainer = document.getElementById(`preference-questions-${containerId}`);
      const saveActions = document.getElementById(`save-actions-${containerId}`);
      if (!preferenceContainer || !saveActions) return;

      // Snapshot existing selections before re-render so they survive the innerHTML reset
      const prevSelections: Record<number, Set<string>> = {};
      preferenceContainer.querySelectorAll('[data-pref-question-index]').forEach((qContainer: any) => {
        const qIndex = parseInt(qContainer.dataset.prefQuestionIndex);
        const question = (this.allPreferenceQuestions || [])[qIndex];
        if (!question) return;
        const checkedSet = new Set<string>();
        qContainer.querySelectorAll('input[type="checkbox"]:checked').forEach((cb: any) => {
          const key = question.type === 'group' ? cb.value : `${cb.dataset.sportId}-${cb.dataset.divisionId}`;
          checkedSet.add(key);
        });
        if (checkedSet.size > 0) prevSelections[qIndex] = checkedSet;
      });

      if (selectedGroupTypes.length === 0 && selectedDivisions.length === 0) {
        preferenceContainer.innerHTML = '';
        saveActions.style.display = 'none';
        return;
      }

      let prefHtml = '<div style="margin-top: 20px;">';
      let hasAnyQuestion = false;
      (this.allPreferenceQuestions || []).forEach((question: any, qIndex: number) => {
        let shouldShow = false;
        if (question.type === 'group' && selectedGroupTypes.includes(question.group_type)) shouldShow = true;
        else if (question.type === 'division_sports' && selectedDivisions.includes(question.division_id)) shouldShow = true;
        if (!shouldShow) return;
        hasAnyQuestion = true;
        prefHtml += `<div class="grouping-question-container" data-pref-question-index="${qIndex}" style="margin-bottom:15px;">
            <p class="mb-10"><strong>${this.escapeHtml(question.question)}</strong></p>
            <div class="grouping-options-list">`;
        if (question.type === 'group') {
          (question.options || []).forEach((option: any) => {
            prefHtml += `<label class="grouping-option-item">
                <input type="checkbox" value="${this.escapeHtml(option.id)}" data-name="${this.escapeHtml(option.name)}" data-group-type="${this.escapeHtml(option.group_type)}">
                <span>${this.escapeHtml(option.name)}</span>
              </label>`;
          });
        } else if (question.type === 'division_sports') {
          (question.sport_options || []).forEach((option: any) => {
            prefHtml += `<label class="grouping-option-item">
                <input type="checkbox" data-sport-id="${this.escapeHtml(option.sport_id)}" data-sport-name="${this.escapeHtml(option.sport_name)}" data-division-id="${this.escapeHtml(option.division_id)}" data-division-name="${this.escapeHtml(option.division_name)}">
                <span>${this.escapeHtml(option.sport_name)} (${this.escapeHtml(option.division_name)})</span>
              </label>`;
          });
        }
        prefHtml += `</div></div>`;
      });
      prefHtml += '</div>';

      if (hasAnyQuestion) {
        preferenceContainer.innerHTML = prefHtml;
        saveActions.style.display = 'block';
        Object.entries(prevSelections).forEach(([qIndexStr, checkedSet]) => {
          const qIndex = parseInt(qIndexStr);
          const question = (this.allPreferenceQuestions || [])[qIndex];
          if (!question) return;
          const container = preferenceContainer.querySelector(`[data-pref-question-index="${qIndex}"]`);
          if (!container) return;
          container.querySelectorAll('input[type="checkbox"]').forEach((cb: any) => {
            const key = question.type === 'group' ? cb.value : `${cb.dataset.sportId}-${cb.dataset.divisionId}`;
            if (checkedSet.has(key)) cb.checked = true;
          });
        });
      } else {
        preferenceContainer.innerHTML = '';
        saveActions.style.display = 'none';
      }
    };

    (window as any).saveAllGroupingPreferences = (containerId: string) => {
      const allGroupSelections: any[] = [];
      const allSportSelections: any[] = [];
      let hasUnansweredVisible = false;
      let unansweredLabel = '';

      (this.allPreferenceQuestions || []).forEach((question: any, qIndex: number) => {
        const container = document.querySelector(`[data-pref-question-index="${qIndex}"]`);
        if (!container) return;
        const checked = container.querySelectorAll('input[type="checkbox"]:checked');
        if (checked.length === 0) {
          hasUnansweredVisible = true;
          unansweredLabel = unansweredLabel || question.question;
          return;
        }
        const selectedNames: string[] = [];
        checked.forEach((cb: any) => {
          if (question.type === 'group') {
            const optionId = parseInt(cb.value);
            const fullOption = (question.options || []).find((o: any) => o.id === optionId);
            allGroupSelections.push(fullOption || { id: optionId, name: cb.dataset.name, group_type: cb.dataset.groupType });
            selectedNames.push(cb.dataset.name);
          } else if (question.type === 'division_sports') {
            const sportId = parseInt(cb.dataset.sportId);
            const divId = parseInt(cb.dataset.divisionId);
            const fullOption = (question.sport_options || []).find((o: any) => o.sport_id === sportId && o.division_id === divId);
            allSportSelections.push(fullOption || { sport_id: sportId, division_id: divId, sport_name: cb.dataset.sportName, division_name: cb.dataset.divisionName });
            selectedNames.push(cb.dataset.sportName);
          }
        });
      });

      if (hasUnansweredVisible) {
        this.toastr.warning(`Please select an answer or uncheck the related option for: "${this.escapeHtml(unansweredLabel)}"`);
        return;
      }
      if (allGroupSelections.length === 0 && allSportSelections.length === 0) {
        this.toastr.warning('Please select at least one preference');
        return;
      }

      document.querySelectorAll(`#grouptype-container-${containerId} input[type="checkbox"]`).forEach((cb: any) => {
        cb.disabled = true;
      });
      const saveBtn = document.querySelector(`#save-actions-${containerId} .save-grouping-btn`) as HTMLButtonElement;
      if (saveBtn) { saveBtn.disabled = true; saveBtn.style.opacity = '0.6'; saveBtn.style.cursor = 'not-allowed'; }

      const mq = this.grouptypeSelectionQuestion;
      let questionText = '';
      if (mq?.question) questionText += `**${mq.question}**  \n`;
      if (mq?.description) questionText += `${mq.description}\n\n`;

      const checkedGroupTypes = new Set<string>();
      document.querySelectorAll(`#grouptype-opts-${containerId} input[type="checkbox"]:checked`).forEach((cb: any) => checkedGroupTypes.add(cb.value));
      const checkedDivisions = new Set<string>();
      document.querySelectorAll(`#division-opts-${containerId} input[type="checkbox"]:checked`).forEach((cb: any) => checkedDivisions.add(cb.value));

      if (mq?.group_type_options?.length) {
        questionText += `**Your Interests:**  \n`;
        mq.group_type_options.forEach((o: any) => {
          questionText += `${checkedGroupTypes.has(o.value) ? '✓' : '☐'} ${o.label}  \n`;
        });
        questionText += '\n';
      }
      if (mq?.division_options?.length) {
        questionText += `**Sports Divisions:**  \n`;
        mq.division_options.forEach((o: any) => {
          questionText += `${checkedDivisions.has(String(o.division_id)) ? '✓' : '☐'} ${o.label}  \n`;
        });
        questionText += '\n';
      }
      (this.allPreferenceQuestions || []).forEach((question: any, qIndex: number) => {
        const prefContainer = document.querySelector(`[data-pref-question-index="${qIndex}"]`);
        if (!prefContainer) return;
        const checkedKeys = new Set<string>();
        prefContainer.querySelectorAll('input[type="checkbox"]:checked').forEach((cb: any) => {
          const key = question.type === 'group' ? cb.value : `${cb.dataset.sportId}-${cb.dataset.divisionId}`;
          checkedKeys.add(key);
        });
        if (checkedKeys.size === 0) return;
        questionText += `**${question.question}**  \n`;
        if (question.type === 'group') {
          (question.options || []).forEach((opt: any) => {
            questionText += `${checkedKeys.has(String(opt.id)) ? '✓' : '☐'} ${opt.name}  \n`;
          });
        } else if (question.type === 'division_sports') {
          (question.sport_options || []).forEach((opt: any) => {
            questionText += `${checkedKeys.has(`${opt.sport_id}-${opt.division_id}`) ? '✓' : '☐'} ${opt.sport_name} (${opt.division_name})  \n`;
          });
        }
        questionText += '\n';
      });

      // Group checkboxes are multi-select with no inherent order - put the most recently
      // checked one first, since saveBulkGroupingPreferences/savedGroupNetworkIds[0] is what
      // becomes network_group_id.
      const mostRecentId = [...this.groupOptionCheckOrder].reverse().find(id =>
        allGroupSelections.some((g: any) => Number(g.id) === id)
      );
      if (mostRecentId != null) {
        const idx = allGroupSelections.findIndex((g: any) => Number(g.id) === mostRecentId);
        if (idx > 0) {
          const [mostRecent] = allGroupSelections.splice(idx, 1);
          allGroupSelections.unshift(mostRecent);
        }
      }

      this.saveBulkGroupingPreferences(allGroupSelections, allSportSelections, [], questionText.trim());
    };

    // ── Theme / settings ──────────────────────────────────────────────────────

    this.subs.add(
      this.commanSubscribeService.getICWebpageThemeSettings().subscribe(res => {
        const urlSegments = this.router.url.split('/');
        const groupName = urlSegments.length > 2 ? urlSegments[2] : null;
        const matched = this.groupsData?.find((g: any) => g.group_slug === groupName);
        if (matched && this.groupsData) {
          this.org_info = { ...this.org_info, channel_logo: matched.group_logo || this.org_info.channel_logo, channel_banner_image: matched.group_agent_banner || this.originalChannelBannerImage };
        } else if (!groupName && this.originalChannelLogo) {
          this.org_info = { ...this.org_info, channel_logo: this.originalChannelLogo, channel_banner_image: this.originalChannelBannerImage };
        }
        this.settingsData = res;
        if (!this.networkName && res?.channelInfo?.channel_display_name) {
          this.networkName = res.channelInfo.channel_display_name;
        }
        if (!this.bannerImage && res?.banner_image) {
          this.bannerImage = res.banner_image;
        }
      })
    );

    // ── User ready ─────────────────────────────────────────────────────────────

    const currentUser = this.commanSubscribeService.userInfo;
    if (currentUser?.id) {
      this.onUserReady(currentUser);
    }

    this.subs.add(
      this.commanSubscribeService.getUserInfo().subscribe(res => {
        if (res?.id) {
          this.onUserReady(res);
        } else {
          const wasSignedIn = !!this.userInfo?.id;
          this.userInfo = null;
          this.fanName = '';
          if (wasSignedIn) this.onUserSignedOut();
          this.cdr.detectChanges();
        }
      })
    );

    // ── Performer info subscription (drives canShowAgent / isAgentEnabled) ─────

    const perfomerInfoData = this.commanSubscribeService.perfomerInfoData;
    this.subs.add(
      this.commanSubscribeService.getPerfomerInfo().subscribe((response: any) => {
        this.onPerformerInfo(response);
      })
    );
    if (Object.keys(perfomerInfoData || {}).length > 0) {
      this.onPerformerInfo(perfomerInfoData);
    }

    // ── Subscription state (drives the no-events banner's Subscribe/Subscribed label) ──
    this.subscribedPlans = this.commanSubscribeService.subscribePayedInfoData || [];
    this.subs.add(
      this.commanSubscribeService.getSubscribePayedInfo().subscribe((plans: any) => {
        this.subscribedPlans = Array.isArray(plans) ? plans : [];
      })
    );

    // ── Trigger from other parts of the app ────────────────────────────────────

    this.subs.add(
      this.commanSubscribeService.getTriggerAiAgent().subscribe((trigger: any) => {
        if (trigger) {
          const currentUrl = this.router.url || '';
          this.isBigbeeHomePage = currentUrl === '' || currentUrl === '/userhome' || currentUrl === '/';
          this.isExpanded = true;
          this.initialMessageSent = false;
          this.messagesList.next([]);
          this.isFirstBotResponse = true;
          this.sendInitialGreeting();
          // Re-fetch grouping preferences too - this app-shell singleton may have already
          // fetched (e.g. via getPerformerOrgDetails on first login) before a preference was
          // just saved elsewhere (e.g. seeded on group signup), or a same-group navigation may
          // have skipped its own refetch. Cheap no-op if nothing actually changed.
          if (this.userInfo?.id && this.org_id) {
            this.fetchGroupingQuestions();
            this.checkAgentPreference();
          }
        }
      })
    );
  }

  ngAfterViewInit(): void {
    document.addEventListener('click', this.handleGlobalClick.bind(this));
  }

  // ── Global click handler (for sign-in links embedded in chat HTML) ──────────

  handleGlobalClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains('signin-trigger')) {
      this.openSignIn();
    }
  }

  private onPerformerInfo(response: any): void {
    const data = response?.data?.Data?.[0];
    if (!data) return;

    if (!this.org_id) this.org_id = data.org_info?.id || data.organization_id;
    if (data.organization_key && data.organization_key !== this.organizationKey) {
      this.organizationKey = data.organization_key;
      this.resetAffiliateData();
    }
    this.loadAffiliateData();
    if (!this.performerId) this.performerId = data.performer_id || '';
    if (!this.channelId) this.channelId = data.plans?.[0]?.channel_id || '';
    if (data.plans) this.channelPlans = data.plans;
    if (!this.channelDisplayName) this.channelDisplayName = data.channel_display_name || '';
    if (!this.businessName) this.businessName = data.org_info?.organization_name || '';
    if (!this.networkName) this.networkName = data.channel_display_name || '';
    if (!this.bannerImage) this.bannerImage = data.org_info?.channel_banner_image || '';
    if (!this.channelName) this.channelName = data.channel_name || '';
    if (!this.displayAgentName) this.displayAgentName = data.agent_name || '';
    if (!this.org_info?.channel_logo && data.org_info) {
      this.org_info = data.org_info;
      this.originalChannelLogo = data.org_info.channel_logo;
      this.originalChannelAgentAvatar = data.org_info.channel_agent_avatar;
      this.org_info.channel_banner_image = this.org_info.channel_agent_banner
        ? this.org_info.channel_agent_banner
        : DEFAULT_IMAGE.IMAGE_3;
      this.originalChannelBannerImage = this.org_info.channel_banner_image;
    }

    const enableAiAgent = data.enable_ai_agent;
    this.isAgentEnabled = enableAiAgent === 1 || enableAiAgent === '1' || enableAiAgent === true;
    this.isGroupSupported = !!data.is_group_supported;

    if (!this.isAgentEnabled) {
      this.canShowAgent = false;
      this.isExpanded = false;
      return;
    }
    // For guests: populate groupsData and detect group context from URL so filtering works
    // without requiring getPerformerOrgDetails (which needs a logged-in user_id).
    if (!this.userInfo?.id && data.groups?.length && !this.groupsData?.length) {
      this.groupsData = data.groups;
      this.groupsLabel = data.network_type_label || 'Group';
      const urlSegments = this.router.url.split('?')[0].split('/').filter(Boolean);
      const urlGroupSlug = urlSegments.length > 1 ? urlSegments[1] : '';
      const matched = urlGroupSlug ? this.groupsData.find((g: any) => g.group_slug === urlGroupSlug) : null;
      if (matched) {
        this.currentMatchedGroup = matched;
        this.currentGroupId = matched.group_id || matched.id;
        this.currentGroupName = matched.group_name || matched.name || '';
        this.currentGroupSlug = matched.group_slug || '';
      }
    }
    // Only load dashboard/history from here when getPerformerOrgDetails was never called (fallback path).
    // Guard with orgInfoLoaded to prevent duplicate calls when the subscription fires
    // immediately after the synchronous perfomerInfoData check in ngOnInit.
    if (!this.orgDetailsFetched && !this.orgInfoLoaded) {
      this.orgInfoLoaded = true;
      this.loadDashboard();
      this.getChannelHome();
      if (this.userInfo?.id) this.loadAllChatHistories();
    }

    // Always check org credits when org_id is available and agent not yet shown.
    // This ensures the agent auto-expands on /:channelName even when getPerformerOrgDetails
    // is in flight (orgDetailsFetched = true), using the faster cached performer data path.
    if (this.org_id && !this.canShowAgent) {
      this.fetchOrgCredits(this.org_id);
    }
  }

  private onUserReady(user: any): void {
    this.userInfo = user;
    this.fanName = user.user_display_name || user.user_first_name;
    if (!this.org_id && user.organization_id) this.org_id = user.organization_id;
    this.loadAffiliateData();

    if (this.channelName && !this.orgDetailsFetched) {
      // getPerformerOrgDetails will call loadDashboard + loadAllChatHistories after org_id is set
      this.getPerformerOrgDetails();
    } else if (this.org_id && this.isAgentEnabled) {
      // Org data already available — refresh recommendations now that user_id is known,
      // so the AI response is personalized instead of the earlier anonymous fetch.
      // Skipped when the agent is disabled: the widget is not rendered, so nothing would use the data.
      this.fetchRecommendedForYou(this.org_id);
      this.loadAllChatHistories();
      // Skip preference/channel calls if getPerformerOrgDetails is already in flight —
      // it will call these with the correct currentGroupId once it resolves.
      if (!this.orgDetailsFetched) {
        this.checkAgentPreference();
        this.checkChannelAgentPreference();
        this.fetchGroupingQuestions();
        this.getChannelHome();
      }
    }
  }

  // Logout navigates in-app (no reload), so this component outlives the session: drop the
  // member's preferences, chat history and open conversation, and reload the dashboard as a
  // guest so recommendations and uReport stop being filtered by the old preferences.
  private onUserSignedOut(): void {
    this.groupProfileSaved = false;
    this.savedGroupItems = [];
    // Sent as network_group_id when no group is routed - left set, the guest feed stays filtered.
    this.savedGroupNetworkIds = [];
    this.selectedSubChannels = [];
    this.chatHistories = [];
    this.createNewChat();
    this.loadDashboard();
  }

  // ── Sign-in dialog ─────────────────────────────────────────────────────────

  openSignIn(): void {
    const dialogRef = this.dialog.open(ConsumerSignInComponent, {
      maxWidth: '450px',
      id: 'mat-box',
      panelClass:
        Object.keys(this.settingsData || {}).length > 0 && this.settingsData.form_bg_color
          ? 'app-consumer-signin'
          : 'app-consumer-sign-in',
      disableClose: true,
      data: {},
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'success') {
        this.userInfo = this.commanSubscribeService.userInfo;
        this.loadAffiliateData();
        if (this.threadId) {
          this.getPreviousMessages();
        }
      }
      if (result === 'signup') {
        this.isExpanded = false;
      }
    });
  }

  // ── Logout ────────────────────────────────────────────────────────────────

  signout(): void {
    this.storageService.deleteItem('eventInfo');
    this.storageService.deleteItem('isEvent');
    this.storageService.deleteItem('ticket_key');

    this.appService.postRequest(API.FAN_LOGOUT, '').subscribe({
      next: (result: any) => {
        if (result?.status === 0) {
          this.storageService.deleteItem('isAffiliateUser');
          this.storageService.deleteItem('user_Info');
          this.storageService.deleteItem('eventInfo');
          this.storageService.deleteItem('signInRouteData');
          this.storageService.deleteItem('redirectUrlAfterGoogleLogin');
          this.commanSubscribeService.sendTriggeredEvent({ actionType: 'sign-out', actionName: 'USER_LOGOUT', organizationId: this.commanSubscribeService.orgId || null });
          this.commanSubscribeService.sendUserInfo({});
          this.commanSubscribeService.sendMylistData([]);
          this.userInfo = {};
          this.fanName = '';
          this.isExpanded = false;
          this.resetAffiliateData();
          sessionStorage.removeItem('oidcAttempted');

          const ONELOGIN_DOMAIN = `${environment.OIDC.onelogin_account_name}.onelogin.com`;
          const POST_LOGOUT_REDIRECT_URI = environment.social_auth_redirect_uri;
          const ID_TOKEN = localStorage.getItem('id_token');

          if (ID_TOKEN) {
            const formattedIdToken = ID_TOKEN.replace(/^"|"$/g, '');
            const logoutUrl =
              `https://${ONELOGIN_DOMAIN}/oidc/2/logout` +
              `?id_token_hint=${encodeURIComponent(formattedIdToken)}` +
              `&post_logout_redirect_uri=${encodeURIComponent(POST_LOGOUT_REDIRECT_URI)}`;
            window.location.href = logoutUrl;
          } else {
            localStorage.clear();
            this.router.navigateByUrl('/' + this.channelName);
          }
        } else {
          this.toastr.error(result?.message || 'Logout failed. Please try again.');
        }
      },
      error: () => {
        this.toastr.error('Logout failed. Please try again.');
      }
    });
  }

  // ── Migrate thread after sign-in ───────────────────────────────────────────

  getPreviousMessages(): void {
    const url = `${API.AI_FAN_AGENT}/migrate_thread?NOLOADER`;
    const formData = new FormData();
    formData.append('user_id', this.userInfo.id);
    formData.append('thread_id', this.threadId);
    formData.append('agent_name', 'channel_fan_agent');
    formData.append('organization_id', this.org_id);
    if (this.currentGroupId) formData.append('group_id', this.currentGroupId);

    this.appService.postBase1Request(url, formData).subscribe({ error: () => { } });
  }

  // ── Performer / org info ────────────────────────────────────────────────────

  private getPerformerOrgDetails(): void {
    if (this.performerOrgDetailsInFlight) return;
    this.performerOrgDetailsInFlight = true;
    this.orgDetailsFetched = true;

    const currency = this.storageService.getItem('currency') || '';
    const data = {
      headers: { 'Content-Type': 'application/json', 'x-api-key': environment.AWS_Cognito.X_Api_Key },
      response: true,
      body: { channel_name: this.channelName, currency, user_id: this.userInfo.id },
    };

    AWS_API.post(environment.AWS_Cognito.apiName, CONST_AWS_API.GET_PERFORMER_INFO, data)
      .then((response: any) => {
        this.ngZone.run(() => {
          this.performerOrgDetailsInFlight = false;
          const data0 = response?.data?.Data?.[0];
          if (data0) {
            this.org_id = data0.org_info?.id || data0.organization_id || this.org_id;
            this.performerId = data0.performer_id || '';
            this.channelId = data0.plans?.[0]?.channel_id || '';
            this.channelDisplayName = data0.channel_display_name || '';
            this.businessName = data0.org_info?.organization_name || '';
            this.networkName = data0.channel_display_name || this.networkName;
            this.bannerImage = data0.org_info?.channel_banner_image || this.bannerImage;

            if (data0.org_info) {
              this.org_info = data0.org_info;
              this.originalChannelLogo = data0.org_info.channel_logo;
              this.org_info.channel_banner_image = this.org_info.channel_agent_banner
                ? this.org_info.channel_agent_banner
                : DEFAULT_IMAGE.IMAGE_3;
              this.originalChannelBannerImage = this.org_info.channel_banner_image;
            }
            this.orgInfoLoaded = true;
            this.groupsData = data0.groups;
            this.groupsLabel = data0.network_type_label || 'Groups';
            this.displayAgentName = data0.agent_name || '';
            this.enrichPrefQuestionsWithLogos();
            // Detect group context from URL
            const urlSegments = this.router.url.split('/');
            const groupSlug = urlSegments.length > 2 ? urlSegments[2] : null;
            const matchedGroup = groupSlug ? this.groupsData?.find((g: any) => g.group_slug === groupSlug) : undefined;
            if (matchedGroup) {
              this.currentMatchedGroup = matchedGroup;
              this.currentGroupId = matchedGroup.group_id || matchedGroup.id;
              this.currentGroupName = matchedGroup.group_name || matchedGroup.name || '';
              this.currentGroupSlug = matchedGroup.group_slug || '';
            }

            const enableAiAgent = data0?.enable_ai_agent;
            this.isAgentEnabled = enableAiAgent === 1 || enableAiAgent === '1' || enableAiAgent === true;
            this.isGroupSupported = !!data0?.is_group_supported;

            if (!this.isAgentEnabled) {
              this.canShowAgent = false;
              this.isExpanded = false;
              // Widget is not rendered for this network, so none of its data is needed.
              return;
            }

            this.loadDashboard();
            this.loadAllChatHistories();

            if (this.userInfo?.id) {
              this.fetchGroupingQuestions();
              this.checkAgentPreference();
              this.checkChannelAgentPreference();
              this.getChannelHome();
              this.fetchOrgCredits(this.org_id);
            }
          }
        });
      })
      .catch(() => {
        this.performerOrgDetailsInFlight = false;
        this.orgInfoLoaded = true;
      });
  }

  // ── Org credits check ──────────────────────────────────────────────────────

  fetchOrgCredits(orgId: string): void {
    // Called from both the performer-info subscription and the org-details response, which can
    // overlap on load; the in-flight request for the same org sets the same state.
    if (this.orgCreditsInFlightFor === orgId) return;
    this.orgCreditsInFlightFor = orgId;
    // Intentionally omit data_status=A: the nightly expiry cron (partner-service crons.js)
    // flips a plan's row to data_status='I' the moment it expires, so filtering to 'A' hides
    // the exact row we need to read expire_date from to compute the grace period.
    const url = `${environment.API_URL_ORG_TIERS}?org_id=${orgId}&page=1&limit=10`;
    this.appService.getOrgDetails(url).subscribe({
      next: (res: any) => {
        this.orgCreditsInFlightFor = null;
        const rows: any[] = res?.result?.data || [];

        // An org can have multiple org_tiers rows (renewal history, an old plan that
        // expired alongside a newer active one, etc). Always prefer a currently-active
        // ('A') row over an expired one — only fall back to the most recently expired
        // row (to evaluate the grace window) when there's no active row at all.
        const activeTier = rows.find((r: any) => r?.data_status === 'A');
        const expiredTiers = rows
          .filter((r: any) => r?.data_status === 'I' || r?.tier_status === 'expired')
          .sort((a: any, b: any) => new Date(b?.expire_date).getTime() - new Date(a?.expire_date).getTime());
        const tier = activeTier || expiredTiers[0];

        const credits: number = activeTier?.available_credits ?? 0;
        this.availableCredits = credits;
        this.expiryDate = tier?.expire_date || null;
        this.isPlanExpired = !activeTier && !!this.expiryDate;

        const visible = canShowAgent({
          serverEnforcement: serverEnforcementEnabled(environment), hasActivePlan: !!activeTier,
          hasExpiredPlan: !!expiredTiers[0], credits, inClientGrace: this.isInGracePeriod,
        });
        if (visible) {
          this.canShowAgent = true;
          const currentUrl = this.router.url || '';
          const isChannelHomeOnly = this.channelName && currentUrl === `/${this.channelName}`;
          if (isChannelHomeOnly && !this.initialMessageSent) {
            this.isExpanded = true;
            this.initialMessageSent = false;
            this.messagesList.next([]);
            this.isFirstBotResponse = true;
            this.sendInitialGreeting();
          }
        } else {
          this.canShowAgent = false;
          this.isExpanded = false;
        }
      },
      error: () => {
        this.orgCreditsInFlightFor = null;
        this.canShowAgent = false;
        this.isExpanded = false;
      }
    });
  }

  // ── Channel home (sub-channels) ────────────────────────────────────────────

  getChannelHome(channelId: number = 0): void {
    if (!this.org_id) return;
    const headers: any = {
      'Content-Type': 'application/json',
      'x-api-key': environment.AWS_Cognito.X_Api_Key,
    };
    if (this.userInfo?.access_token) {
      headers['Authorization'] = `Bearer ${this.userInfo.access_token}`;
    }
    const effectiveChannelId = channelId || Number(this.channelId) || 0;
    const data = {
      headers,
      response: true,
      body: {
        performer_id: this.performerId || null,
        organization_id: this.org_id,
        channel_id: effectiveChannelId,
        network_group_id: this.commanSubscribeService.activeGroupId || undefined,
      },
    };
    AWS_API.post(environment.AWS_Cognito.apiName, CONST_AWS_API.GET_CHANNEL_HOME, data)
      .then((response: any) => {
        this.ngZone.run(() => {
          if (response?.data?.statusCode !== '200') return;
          if (response.data.sub_channels?.length > 0) {
            this.subChannels = response.data.sub_channels.map((ch: any) => ({
              ...ch,
              channel_logo: ch.channel_logo || ch.logo || ch.channel_image || '',
            }));
            this.preselectSavedChannels();
          }
        });
      })
      .catch(() => { });
  }

  // ── Preference checks ──────────────────────────────────────────────────────

  checkAgentPreference(triggerMsg = false): void {
    if (!this.userInfo?.id || !this.org_id) return;
    if (this.preferencesCheckInFlight) return;
    this.preferencesCheckInFlight = true;

    const formData = new FormData();
    formData.append('user_id', this.userInfo.id);
    formData.append('organization_id', this.org_id);
    if (this.currentGroupId) {
      formData.append('group_id', this.currentGroupId);
    }

    this.appService.postBase1Request(`${API.AI_FAN_AGENT_PREFERENCES}?NOLOADER`, formData).subscribe({
      next: (response: any) => {
        this.preferencesCheckInFlight = false;
        if (response) {
          this.hasPreferred = response.has_preferences;
          this.preferredObj = response;
          if (!this.isEditingGroupProfile) {
            this.applyGroupPreferencesFromResponse(response);
          }
          if (triggerMsg) {
            if (response.response) {
              setTimeout(() => this.getPersonalizeMsg(), 500);
            }
          } else {
            // Early call — show message if sendInitialGreeting has already run
            const chatVisible = this.messagesList.getValue().length > 0 || this.initialMessageSent;
            const groupingDone = this.groupInfo?.status === 'complete' || !this.groupInfo?.total_questions;
            if (chatVisible) {
              if (this.currentGroupId) {
                if (response.response) {
                  setTimeout(() => this.getPersonalizeMsg(), 500);
                }
              } else if (groupingDone && !this.groupTypeDisplayed &&
                !response.has_preferences && response.has_org_questions) {
                setTimeout(() => this.getPersonalizeMsg(), 500);
              }
            }
          }
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.preferencesCheckInFlight = false;
      }
    });
  }

  checkChannelAgentPreference(): void {
    if (!this.userInfo?.id || !this.org_id) return;

    const url = `${API.AI_FAN_AGENT_PREFERRED_CHANNELS}?NOLOADER`;
    const formData = new FormData();
    formData.append('user_id', this.userInfo.id);
    formData.append('organization_id', this.org_id);

    this.appService.postBase1Request(url, formData).subscribe({
      next: (response: any) => {
        this.hasPreferredChannel = response?.has_preferred_channel;
        const ids = response?.preferred_channels_ids;
        const names = response?.preferred_channels;
        if (ids?.length) {
          this.savedPreferredChannelIds = ids.map((id: any) => Number(id));
          this.preselectSavedChannels();
          this.getChannelHome(this.savedPreferredChannelIds[0]);
        } else if (names?.length) {
          this.savedPreferredChannelNames = Array.isArray(names) ? names : [names];
          this.preselectSavedChannels();
          if (this.savedPreferredChannelIds.length) {
            this.getChannelHome(this.savedPreferredChannelIds[0]);
          }
        }
      },
      error: () => { }
    });
  }

  preselectSavedChannels(): void {
    if (!this.subChannels?.length) return;
    if (this.savedPreferredChannelIds.length) {
      this.selectedSubChannels = this.subChannels.filter((c: any) =>
        this.savedPreferredChannelIds.includes(Number(c.channel_id))
      );
    } else if (this.savedPreferredChannelNames.length) {
      const normalizedNames = this.savedPreferredChannelNames.map((n: string) => n?.toLowerCase().trim());
      this.selectedSubChannels = this.subChannels.filter((c: any) => {
        const name = (c.channel_name || '').toLowerCase().trim();
        const displayName = (c.channel_display_name || '').toLowerCase().trim();
        return normalizedNames.includes(name) || normalizedNames.includes(displayName);
      });
      this.savedPreferredChannelIds = this.selectedSubChannels.map((c: any) => Number(c.channel_id));
      if (this.savedPreferredChannelIds.length) {
        this.getChannelHome(this.savedPreferredChannelIds[0]);
      }
    } else {
      return;
    }
    this.channelProfileSaved = this.selectedSubChannels.length > 0;
    this.isEditingChannelProfile = false;
    // Same as applyGroupPreferencesFromResponse - this syncs already-saved preferred channels
    // on page load, so re-filter locally instead of racing a second dashboard fetch.
    this.reapplyCachedRecommendations();
    this.cdr.detectChanges();
  }

  buildRecommendItems() {
    this.recommendedItems = [
      this.aiRecommendedEvent,
      this.aiRecommendedContent,
      this.aiRecommendedMerch,
      this.aiRecommendedCommunity,
    ].filter(Boolean).slice(0, 4);
    this.cdr.detectChanges();
  }

  private applyGroupPreferencesFromResponse(data: any): void {
    if (!data) return;

    const groupTypeSet = new Set<string>();
    const divisionSet = new Set<number>();

    (data.preferable_groups || []).forEach((groupObj: any) => {
      Object.keys(groupObj).forEach(groupType => groupTypeSet.add(groupType));
    });
    // preferable_sports entries carry no division_id of their own (see selectedSportIds below) -
    // divisions come exclusively from preferable_divisions.
    (data.preferable_divisions || []).forEach((division: any) => {
      if (division.division_id != null) divisionSet.add(Number(division.division_id));
    });
    const hasEventCategoryPrefs = (data.preferable_event_categories || []).length > 0;
    const hasGenrePrefs = (data.preferable_vod_tags || []).length > 0;
    const hasEventTagPrefs = (data.preferable_event_tags || []).length > 0;

    // A fan who has only ever answered the category/event_type, genre, or event_tag question
    // (no Team/Sport at all) still has real saved preferences - don't bail out before
    // savedGroupItems gets a chance to include them below.
    if (groupTypeSet.size === 0 && divisionSet.size === 0 && !hasEventCategoryPrefs && !hasGenrePrefs && !hasEventTagPrefs) return;

    // Resolve raw preferable_groups key → canonical opt.value from grouptypeSelectionQuestion.
    // The stored key (e.g. "TEAM") may differ in case/format from opt.value (e.g. "country_rights_group").
    const groupTypeOptions: any[] = this.grouptypeSelectionQuestion?.group_type_options || [];
    const resolveGroupType = (rawKey: string): string => {
      if (!groupTypeOptions.length) return rawKey;
      const matched = groupTypeOptions.find(
        (opt: any) =>
          opt.value === rawKey ||
          opt.value?.toUpperCase() === rawKey?.toUpperCase() ||
          opt.label?.toUpperCase() === rawKey?.toUpperCase() ||
          opt.label?.toLowerCase().replace(/\s+/g, '_') === rawKey?.toLowerCase()
      );
      if (matched) return matched.value;

      // Fallback: preferable_groups key may be the group_type_label of an option (e.g. "Event Group")
      // rather than the canonical value ("event_group") or the group-type label ("Group1").
      // Search through preference question options for a matching group_type_label.
      const prefQuestions = data.all_preference_questions || data.preference_questions || [];
      for (const q of prefQuestions) {
        if (q.type === 'group') {
          const optMatch = (q.options || []).find(
            (o: any) => o.group_type_label?.toUpperCase() === rawKey?.toUpperCase()
          );
          if (optMatch?.group_type) return optMatch.group_type;
        }
      }

      // Fans seeded at signup before the seed saved the hero panel's own option have their group
      // stored under the performer-info group_type (e.g. "country_rights_group"), which matches
      // no option above. Map that key to the group question that actually lists these group ids,
      // otherwise a duplicate section gets synthesized for it below.
      const savedIds = new Set<number>((data.preferable_groups || []).flatMap((groupObj: any) =>
        (groupObj?.[rawKey] || []).map((g: any) => Number(g?.id))
      ));
      const owningQuestion = [...prefQuestions, ...(this.allPreferenceQuestions || [])].find((q: any) =>
        q.type === 'group' && (q.options || []).some((o: any) => savedIds.has(Number(o?.id)))
      );
      if (owningQuestion?.group_type) {
        const owningOpt = groupTypeOptions.find(
          (opt: any) =>
            opt.value === owningQuestion.group_type ||
            opt.value?.toUpperCase() === owningQuestion.group_type?.toUpperCase() ||
            opt.label?.toUpperCase() === owningQuestion.group_type?.toUpperCase()
        );
        return owningOpt?.value || owningQuestion.group_type;
      }

      return rawKey;
    };

    const rawToResolved = new Map<string, string>();
    groupTypeSet.forEach(rawKey => rawToResolved.set(rawKey, resolveGroupType(rawKey)));

    if (groupTypeSet.size > 0) this.heroGroupTypeSelections = [...new Set(rawToResolved.values())];
    if (divisionSet.size > 0) this.heroGroupDivisionSelections = [...divisionSet];

    let allPrefs: any[] = this.allPreferenceQuestions.length
      ? [...this.allPreferenceQuestions]
      : [...(data.all_preference_questions || data.preference_questions || [])];

    // Normalize any existing group questions to canonical group_type + proper question text
    let prefsNeedUpdate = false;
    if (groupTypeOptions.length) {
      allPrefs = allPrefs.map((q: any) => {
        if (q.type !== 'group') return q;
        const matched = groupTypeOptions.find(
          (opt: any) =>
            opt.value === q.group_type ||
            opt.value?.toUpperCase() === q.group_type?.toUpperCase() ||
            opt.label?.toUpperCase() === q.group_type?.toUpperCase()
        );
        if (!matched) return q;
        const canonicalType = matched.value;
        const canonicalQuestion = `SELECT YOUR PREFERRED ${matched.label.toUpperCase()}`;
        if (q.group_type !== canonicalType || q.question !== canonicalQuestion) {
          prefsNeedUpdate = true;
          return { ...q, group_type: canonicalType, question: canonicalQuestion };
        }
        return q;
      });
    }

    // Synthesize missing group questions from preferable_groups data
    (data.preferable_groups || []).forEach((groupObj: any) => {
      Object.keys(groupObj).forEach(rawGroupType => {
        const resolvedType = rawToResolved.get(rawGroupType) || rawGroupType;
        const exists = allPrefs.some(
          (q: any) => q.type === 'group' &&
            (q.group_type === resolvedType || q.group_type?.toUpperCase() === resolvedType?.toUpperCase())
        );
        if (!exists) {
          const matchedOpt = groupTypeOptions.find((opt: any) => opt.value === resolvedType);
          const questionText = matchedOpt
            ? `SELECT YOUR PREFERRED ${matchedOpt.label.toUpperCase()}`
            : rawGroupType.replace(/_/g, ' ');
          allPrefs.push({
            type: 'group',
            group_type: resolvedType,
            question: questionText,
            options: (groupObj[rawGroupType] || []).map((g: any) => ({
              id: g.id, name: g.group_name || g.name, group_type: resolvedType
            }))
          });
          prefsNeedUpdate = true;
        }
      });
    });

    if (prefsNeedUpdate || allPrefs.length !== this.allPreferenceQuestions.length) {
      this.allPreferenceQuestions = allPrefs;
    }

    const prefSelections: Record<number, string[]> = {};

    (data.preferable_groups || []).forEach((groupObj: any) => {
      Object.keys(groupObj).forEach(rawGroupType => {
        const resolvedType = rawToResolved.get(rawGroupType) || rawGroupType;
        const selectedIds = (groupObj[rawGroupType] || []).map((g: any) => String(g.id));
        if (selectedIds.length) {
          const qIndex = allPrefs.findIndex(
            (q: any) => q.type === 'group' &&
              (q.group_type === resolvedType ||
                q.group_type?.toUpperCase() === resolvedType?.toUpperCase() ||
                q.group_type === rawGroupType ||
                q.group_type?.toUpperCase() === rawGroupType?.toUpperCase())
          );
          // preferable_groups can contain multiple entries that resolve to the same group_type
          // (and therefore the same qIndex) - merge rather than overwrite, otherwise only the
          // last-processed entry's groups end up highlighted in edit mode even though
          // savedGroupItems (built via flatMap over every entry) correctly shows them all.
          if (qIndex >= 0) {
            prefSelections[qIndex] = [...new Set([...(prefSelections[qIndex] || []), ...selectedIds])];
          }
        }
      });
    });

    // preferable_sports entries carry no division_id of their own (a sport can exist under
    // multiple divisions, e.g. MMA under both Men's and Women's) - only highlight a sport
    // option within a division the fan actually selected (divisionSet, from preferable_divisions).
    const selectedSportIds = new Set<number>(
      (data.preferable_sports || [])
        .map((s: any) => Number(s.sport_id))
        .filter((id: number) => !Number.isNaN(id))
    );
    if (selectedSportIds.size) {
      allPrefs.forEach((q: any, qIndex: number) => {
        if (q.type !== 'division_sports' || !divisionSet.has(Number(q.division_id))) return;
        const matchedKeys = (q.sport_options || [])
          .filter((opt: any) => selectedSportIds.has(Number(opt.sport_id)))
          .map((opt: any) => `${opt.sport_id}-${opt.division_id}`);
        if (matchedKeys.length) {
          prefSelections[qIndex] = [...(prefSelections[qIndex] || []), ...matchedKeys];
        }
      });
    }

    this.heroGroupPreferenceSelections = { ...this.heroGroupPreferenceSelections, ...prefSelections };
    this.refreshVisibleHeroPreferenceQuestions();

    this.groupProfileSaved = true;
    this.isEditingGroupProfile = false;
    // The same group can be saved under two keys (legacy signup seed + hero panel) - list it once.
    const seenGroupIds = new Set<number>();
    this.savedGroupItems = [
      ...(data.preferable_groups || []).flatMap((groupObj: any) =>
        Object.values(groupObj).flat().filter((g: any) => {
          const id = Number((g as any)?.id);
          if (!id) return true;
          if (seenGroupIds.has(id)) return false;
          seenGroupIds.add(id);
          return true;
        }).map((g: any) => {
          const gd = this.groupsData?.find((d: any) => Number(d.group_id || d.id) === Number((g as any).id));
          // is_active is absent on non-group entries and older responses - default true so
          // nothing renders as greyed out unless the backend explicitly says it's hidden.
          return {
            name: (g as any).group_name || (g as any).name,
            logo: gd?.group_base_logo || '',
            is_active: (g as any).is_active !== false,
          };
        })
      ),
      // A division picked with no specific sport is persisted with no sport_name at all
      // (see saveHeroGroupingPreferences) - fall back to division_name so this rehydrated
      // summary (rebuilt from the server's saved data on load) doesn't render blank. The Edit
      // picker itself is unaffected since it's driven by heroGroupDivisionSelections, not this.
      ...(data.preferable_sports || []).map((s: any) => ({ name: s.sport_name || s.division_name || 'Division', logo: '' })),
      ...(data.preferable_divisions || []).map((d: any) => ({ name: d.division_name || 'Division', logo: '' })),
      ...(data.preferable_event_categories || []).map((ec: any) => ({
        name: ec.event_type ? `${ec.event_type} (${ec.category_name})` : ec.category_name, logo: ''
      })),
      ...(data.preferable_vod_tags || []).map((g: any) => ({ name: g.name, logo: '' })),
      ...(data.preferable_event_tags || []).map((et: any) => ({ name: et.tag_name, logo: '' })),
    ];

    // Extract filter IDs from saved preferences (still used by getChannelHome's own filtering).
    // Excludes hidden (is_active=false) groups - the backend now includes them in
    // preferable_groups (for the greyed-out display), but they must not be used to scope
    // network-level content, per Group-Level Content Visibility.
    const groupIds = (data.preferable_groups || []).flatMap((groupObj: any) =>
      Object.values(groupObj).flat()
        .filter((g: any) => g.is_active !== false)
        .map((g: any) => Number((g as any).id))
    ).filter(Boolean);
    const hasSportPrefs = (data.preferable_sports || []).length > 0;

    if (groupIds.length || hasSportPrefs) {
      this.savedGroupNetworkIds = groupIds;
      // Refetch (not just a local re-filter) - the API strictly scopes results server-side
      // when network_group_id is passed, and fetchRecommendedForYou now sends the saved
      // preferred group automatically once savedGroupNetworkIds is set above, so this gets
      // a properly-scoped response instead of hoping the already-cached data contains it.
      // The dashboardCallId guard on fetchRecommendedForYou ensures this always wins over any
      // still-in-flight earlier request (e.g. the initial unfiltered loadDashboard call).
      if (this.org_id) this.fetchRecommendedForYou(this.org_id);
    }

    this.cdr.detectChanges();
  }

  // ── Hero-card grouping UI ─────────────────────────────────────────────────

  heroGroupTypeSelections: string[] = [];
  heroGroupDivisionSelections: number[] = [];
  heroGroupPreferenceSelections: Record<number, string[]> = {};
  // Snapshot of the saved selections taken when entering edit mode, so cancelling
  // (the X button, or minimizing mid-edit) can restore exactly what was there before.
  private savedHeroGroupTypeSelections: string[] = [];
  private savedHeroGroupDivisionSelections: number[] = [];
  private savedHeroGroupPreferenceSelections: Record<number, string[]> = {};
  isSavingHeroGroupPreferences = false;
  groupProfileSaved = false;
  isEditingGroupProfile = false;
  isNavOpen: boolean = true;
  isHubOpen: boolean = true;
  // is_active=false -> saved but currently hidden from the network (Group-Level Content
  // Visibility) - greyed out/disabled in the UI, never removed. Undefined/true = active.
  savedGroupItems: { name: string; logo?: string; is_active?: boolean }[] = [];

  // Right-side preferences panel priority: groups, else channels, else categories/sports.
  // Only one tier is ever shown at a time for the groups-vs-channels split, but category/sports
  // data (hasCategoryData) can render alongside groups when both are present for the org.
  get preferenceDisplayMode(): 'groups' | 'channels' | 'categories' | null {
    if (this.groupsData?.length) return 'groups';
    if (this.subChannels?.length) return 'channels';
    return this.hasCategoryData ? 'categories' : null;
  }

  get hasCategoryData(): boolean {
    return !!this.grouptypeSelectionQuestion?.division_options?.length ||
      (this.allPreferenceQuestions || []).some((q: any) => q.type === 'division_sports');
  }

  // True once anything has been saved on either side, so the channels picker (which has no
  // "saved chips" state of its own when the user never picked a channel) knows to stay
  // collapsed instead of always falling back to its open picker.
  get anyPreferencesSaved(): boolean {
    return this.channelProfileSaved || this.groupProfileSaved;
  }

  // Channels and groups/divisions each have their own edit state, but share a single
  // "Save my preferences" button (see saveAllPreferences) instead of one button each.
  // Editable (picker shown) when actively editing, or when nothing has been picked yet AND
  // nothing has been saved on either side (first-time/default state) - once anything is saved,
  // an unpicked channels section stays collapsed until Edit reopens it (see onEditAllPreferences).
  private get isChannelsSectionEditable(): boolean {
    return this.preferenceDisplayMode === 'channels' &&
      (this.isEditingChannelProfile || (this.selectedSubChannels.length === 0 && !this.anyPreferencesSaved));
  }

  get isGroupsSectionEditable(): boolean {
    const isVisible = (this.preferenceDisplayMode === 'groups' || this.preferenceDisplayMode === 'categories' ||
      (this.preferenceDisplayMode === 'channels' && this.hasCategoryData)) &&
      !!this.groupInfo?.total_questions && !!this.grouptypeSelectionQuestion;
    return isVisible && (!this.groupProfileSaved || this.isEditingGroupProfile) &&
      (this.heroGroupTypeSelections.length > 0 || this.heroGroupDivisionSelections.length > 0);
  }

  get showCombinedPreferencesSave(): boolean {
    return this.isChannelsSectionEditable || this.isGroupsSectionEditable;
  }

  get combinedPreferencesSaveDisabled(): boolean {
    return this.selectedSubChannels.length === 0 &&
      this.heroGroupTypeSelections.length === 0 && this.heroGroupDivisionSelections.length === 0;
  }

  get isSavingCombinedPreferences(): boolean {
    return this.isSavingChannelProfile || this.isSavingHeroGroupPreferences;
  }

  saveAllPreferences(): void {
    // If a section was opened via the shared Edit button but ends up with nothing to save
    // (e.g. channels left untouched while only updating divisions/sports), just exit its edit
    // mode instead of leaving it stuck open - otherwise it never collapses back after saving.
    if (this.isChannelsSectionEditable && this.selectedSubChannels.length > 0) {
      this.saveChannelProfile();
    } else if (this.isEditingChannelProfile) {
      this.isEditingChannelProfile = false;
    }
    if (this.isGroupsSectionEditable) {
      this.saveHeroGroupingPreferences();
    } else if (this.isEditingGroupProfile) {
      this.isEditingGroupProfile = false;
    }
  }

  isHeroGroupTypeSelected(value: string): boolean {
    // Case-insensitive direct match (stored key may differ in case/format from opt.value)
    if (this.heroGroupTypeSelections.some(v => v === value || v?.toUpperCase() === value?.toUpperCase())) return true;
    // Fallback: if any group question for this type has saved selections, treat it as selected
    const idx = (this.allPreferenceQuestions || []).findIndex(
      (q: any) => q.type === 'group' &&
        (q.group_type === value || q.group_type?.toUpperCase() === value?.toUpperCase())
    );
    return idx >= 0 && (this.heroGroupPreferenceSelections[idx]?.length || 0) > 0;
  }

  toggleHeroGroupType(value: string): void {
    const isSelected = this.isHeroGroupTypeSelected(value);
    this.heroGroupTypeSelections = isSelected
      ? this.heroGroupTypeSelections.filter(v => v !== value && v?.toUpperCase() !== value?.toUpperCase())
      : [...this.heroGroupTypeSelections, value];
    if (isSelected) {
      const updated = { ...this.heroGroupPreferenceSelections };
      (this.allPreferenceQuestions || []).forEach((q: any, idx: number) => {
        if (q.type === 'group' && (q.group_type === value || q.group_type?.toUpperCase() === value?.toUpperCase())) {
          delete updated[idx];
        }
      });
      this.heroGroupPreferenceSelections = updated;
    }
    this.refreshVisibleHeroPreferenceQuestions();
  }

  // division_id isn't reliably typed consistently across sources (rehydrated selections are
  // coerced to Number - see applyGroupPreferencesFromResponse - but a division_id clicked
  // straight from the template's opt.division_id may still be a string from the API response),
  // same reason every other division_id comparison in this file (e.g. getVisibleSportOptions,
  // line ~1910) coerces both sides with Number() instead of a strict-equality check.
  isHeroGroupDivisionSelected(divId: number): boolean {
    return this.heroGroupDivisionSelections.some(id => Number(id) === Number(divId));
  }

  toggleHeroGroupDivision(divId: number): void {
    divId = Number(divId);
    const isSelected = this.isHeroGroupDivisionSelected(divId);
    this.heroGroupDivisionSelections = isSelected
      ? this.heroGroupDivisionSelections.filter(id => Number(id) !== Number(divId))
      : [...this.heroGroupDivisionSelections, divId];
    if (isSelected) {
      const updated = { ...this.heroGroupPreferenceSelections };
      (this.allPreferenceQuestions || []).forEach((q: any, idx: number) => {
        if (q.type === 'division_sports' && Number(q.division_id) === Number(divId)) delete updated[idx];
      });
      this.heroGroupPreferenceSelections = updated;
    }
    this.refreshVisibleHeroPreferenceQuestions();
  }

  // GET_PERFORMER_INFO's groups[] carry a categories_sports array (e.g. Hyderabad/id 76:
  // [{category_id: 79, sport_ids: [35,44]}, {category_id: 58, sport_ids: [35]}]) that scopes
  // which divisions/sports are relevant to that specific group. Most groups have it empty
  // ([]). Returns null ONLY when no group is selected yet at all (nothing to restrict by
  // yet, so show every division/sport as before). Once any group is selected, this always
  // returns a real (possibly empty) restriction built from whichever selected group(s) define
  // one - a selected group with no categories_sports of its own contributes nothing of its
  // own, but does NOT fall back to "show everything": if Hyderabad (which has data) is
  // selected together with another group that has none, the result stays scoped to
  // Hyderabad's categories/sports; if only a group with no data is selected, the result is an
  // empty restriction, correctly hiding every division/sport rather than showing them all.
  private getAllowedCategorySportsForSelectedGroups(): { categoryIds: Set<number>; sportIdsByCategory: Map<number, Set<number>> } | null {
    const selectedGroupIds = new Set<number>();
    (this.allPreferenceQuestions || []).forEach((q: any, idx: number) => {
      if (q.type === 'group') {
        (this.heroGroupPreferenceSelections[idx] || []).forEach((key: string) => selectedGroupIds.add(Number(key)));
      }
    });
    if (!selectedGroupIds.size) return null;

    const categoryIds = new Set<number>();
    const sportIdsByCategory = new Map<number, Set<number>>();

    selectedGroupIds.forEach(groupId => {
      const group = this.groupsData?.find((g: any) => Number(g.id || g.group_id) === groupId);
      (group?.categories_sports || []).forEach((entry: any) => {
        const categoryId = Number(entry.category_id);
        categoryIds.add(categoryId);
        const sportIds = sportIdsByCategory.get(categoryId) || new Set<number>();
        (entry.sport_ids || []).forEach((sportId: any) => sportIds.add(Number(sportId)));
        sportIdsByCategory.set(categoryId, sportIds);
      });
    });

    return { categoryIds, sportIdsByCategory };
  }

  // Currently-checked group option ids across every 'group' question - the live, in-progress
  // selection (not the saved preference), used to filter category/event_type/genre options.
  private getSelectedGroupIds(): Set<number> {
    const selectedGroupIds = new Set<number>();
    (this.allPreferenceQuestions || []).forEach((q: any, idx: number) => {
      if (q.type === 'group') {
        (this.heroGroupPreferenceSelections[idx] || []).forEach((key: string) => selectedGroupIds.add(Number(key)));
      }
    });
    return selectedGroupIds;
  }

  // Currently-checked division/sport ids across every 'division_sports' question - mirrors
  // getSelectedGroupIds, but a division_sports selection key is 'sportId-divisionId' (see the
  // template's toggleHeroPreference call), so both ids come out of the same selected keys.
  private getSelectedDivisionIds(): Set<number> {
    // Seed with heroGroupDivisionSelections (a division checked on its own via the Sports
    // Divisions buttons, no sport picked yet) - division_sports keys alone would miss this,
    // since a 'sportId-divisionId' key only exists once a specific sport is also checked.
    const selectedDivisionIds = new Set<number>(this.heroGroupDivisionSelections.map(id => Number(id)));
    (this.allPreferenceQuestions || []).forEach((q: any, idx: number) => {
      if (q.type === 'division_sports') {
        (this.heroGroupPreferenceSelections[idx] || []).forEach((key: string) => {
          const divisionId = Number(key.split('-')[1]);
          if (!isNaN(divisionId)) selectedDivisionIds.add(divisionId);
        });
      }
    });
    return selectedDivisionIds;
  }

  private getSelectedSportIds(): Set<number> {
    const selectedSportIds = new Set<number>();
    (this.allPreferenceQuestions || []).forEach((q: any, idx: number) => {
      if (q.type === 'division_sports') {
        (this.heroGroupPreferenceSelections[idx] || []).forEach((key: string) => {
          const sportId = Number(key.split('-')[0]);
          if (!isNaN(sportId)) selectedSportIds.add(sportId);
        });
      }
    });
    return selectedSportIds;
  }

  // Shared visibility rule for category/event_type/genre/event_tag options: each option
  // carries network_group_ids (0 = network-wide). An option shows only if it's network-wide OR
  // belongs to a currently-checked group - no special case for "nothing checked yet": with
  // no group selected, only network-wide options pass, so a group-only option (e.g. Cycling
  // Sports tagged only to group 75) correctly stays hidden until that group is checked.
  // Additive as more groups get checked, not exclusive (checking a second group adds its
  // options, doesn't replace the first group's). This is the rule for category/event_type/
  // event_tag, and for genre when `strict` is false.
  //
  // Genre options instead carry network_combinations: [{group_id, division_id, sport_id}, ...] -
  // every DISTINCT exact combination the tag is actually associated with (see backend's
  // fetch_genre_options), since the SAME tag can have multiple real combinations (e.g. one item
  // scoped to group 110 only, another to group 110 + division 84 + sport 45) that must NOT be
  // flattened into independent per-facet arrays - that would wrongly imply cross-combinations
  // that were never real (e.g. division 84 with sport 0). Matching is OR-across-combinations,
  // AND-within-each-combination: the option shows if AT LEAST ONE of its combinations
  // simultaneously satisfies all three facets under the exclusive-narrowing rule genre uses -
  // when the fan checks something in a facet, only an exact match passes there. When a facet is
  // UNPICKED, it's a full wildcard (Amendment 4 - group/division/sport are handled identically,
  // no facet-specific special-casing): picking a broader scope (e.g. a division alone) surfaces
  // every tag at-or-below it, from any group, not just ones tagged group=0. So checking a group
  // hides every OTHER group's genres in favor of that group's own (at every division/sport),
  // and additionally checking a division on top narrows further to just that group+division pair
  // (division is now picked, so its wildcard no longer applies) - each pick narrows once it's
  // actually picked, it doesn't just add. A fan who picks a Division/Sport with no Group sees
  // that division/sport's genres from every group, including network-level (group=0) ones - Group
  // only ever restricts the result once the fan actually picks one. A facet that's undefined on
  // the option (not present at all, vs. present as [0]) is untouched by `strict` and always
  // passes. Nothing picked at all falls out of this same rule automatically (every facet
  // wildcards), so every genre shows - no special-case guard needed.
  filterOptionsByCurrentGroup(options: any[], strict: boolean = false): any[] {
    const selectedGroupIds = this.getSelectedGroupIds();
    const selectedDivisionIds = this.getSelectedDivisionIds();
    const selectedSportIds = this.getSelectedSportIds();

    const facetOk = (ids: number[] | undefined, selected: Set<number>): boolean => {
      if (!ids) return true;
      if (!strict) return ids.some((id) => id === 0 || selected.has(Number(id)));
      return selected.size === 0 ? true : ids.some((id) => selected.has(Number(id)));
    };

    // One exact facet value (as carried by a network_combinations entry) against the same
    // narrowing rule facetOk applies to an array - always strict, since a combination pins each
    // facet to one specific real value, never a "same or wildcard" set. Group/division/sport are
    // handled identically (Amendment 4): unpicked -> any value passes, picked -> must intersect.
    const comboFacetOk = (id: number, selected: Set<number>): boolean =>
      selected.size === 0 ? true : selected.has(Number(id));

    return (options || []).filter((opt: any) => {
      if (Array.isArray(opt.network_combinations)) {
        return opt.network_combinations.some((combo: any) =>
          comboFacetOk(combo.group_id, selectedGroupIds) &&
          comboFacetOk(combo.division_id, selectedDivisionIds) &&
          comboFacetOk(combo.sport_id, selectedSportIds)
        );
      }
      if (!facetOk(opt.network_group_ids || [0], selectedGroupIds)) return false;
      if (!facetOk(opt.network_division_ids, selectedDivisionIds)) return false;
      return facetOk(opt.network_sport_ids, selectedSportIds);
    });
  }

  // event_tag options are keyed by genre_id when the backend resolved one against getAllGeners,
  // else by the normalized (trimmed, lowercased) tag name - mirrors the backend's _event_tag_key.
  eventTagKey(opt: any): string {
    return opt.genre_id != null ? String(opt.genre_id) : (opt.tag_name || '').trim().toLowerCase();
  }

  // Sports Divisions buttons - restricted to the selected group(s)' own categories when they
  // define one, otherwise every division stays visible (unchanged default behavior).
  get visibleDivisionOptions(): any[] {
    const options = this.grouptypeSelectionQuestion?.division_options || [];
    const allowed = this.getAllowedCategorySportsForSelectedGroups();
    if (!allowed) return options;
    return options.filter((opt: any) => allowed.categoryIds.has(Number(opt.division_id)));
  }

  // Sport options within a division_sports question - same restriction, applied per-sport
  // using the specific category's allowed sport_ids (not just "is this category allowed").
  getVisibleSportOptions(question: any): any[] {
    const options = question?.sport_options || [];
    const allowed = this.getAllowedCategorySportsForSelectedGroups();
    if (!allowed) return options;
    return options.filter((opt: any) => {
      const divisionId = Number(opt.division_id);
      if (!allowed.categoryIds.has(divisionId)) return false;
      const sportIds = allowed.sportIdsByCategory.get(divisionId);
      return sportIds ? sportIds.has(Number(opt.sport_id)) : true;
    });
  }

  // Called once after groupsData + allPreferenceQuestions are both available
  enrichPrefQuestionsWithLogos(): void {
    if (!this.allPreferenceQuestions?.length || !this.groupsData?.length) return;
    this.allPreferenceQuestions.forEach((q: any) => {
      if (q.type === 'group' && q.options?.length) {
        q.options.forEach((opt: any) => {
          if (!opt.logo) {
            const match = this.groupsData.find((gd: any) =>
              Number(gd.group_id || gd.id) === Number(opt.id) ||
              (gd.group_name || gd.name || '').toLowerCase() === (opt.name || '').toLowerCase()
            );
            if (match?.group_base_logo) opt.logo = match.group_base_logo;
          }
        });
      }
    });
  }

  visibleHeroPreferenceQuestions: any[] = [];

  refreshVisibleHeroPreferenceQuestions(): void {
    this.visibleHeroPreferenceQuestions = (this.allPreferenceQuestions || [])
      .map((q: any, index: number) => ({ ...q, _qIndex: index }))
      .filter((q: any) => {
        if (q.type === 'group') {
          return this.heroGroupTypeSelections.some(
            v => v === q.group_type || v?.toUpperCase() === q.group_type?.toUpperCase()
          ) || (this.heroGroupPreferenceSelections[q._qIndex]?.length || 0) > 0;
        }
        // Default: show every allowed division's sports (before any division is picked).
        // Once the fan selects one or more divisions, narrow down to only those divisions'
        // sports - toggling a division off already prunes its sport selections (see
        // toggleHeroGroupDivision).
        if (q.type === 'division_sports') {
          const isAllowed = this.visibleDivisionOptions.some((d: any) => Number(d.division_id) === Number(q.division_id));
          if (!isAllowed) return false;
          if (this.heroGroupDivisionSelections.length === 0) return true;
          return this.isHeroGroupDivisionSelected(Number(q.division_id)) ||
            (this.heroGroupPreferenceSelections[q._qIndex]?.length || 0) > 0;
        }
        // The category picker has no upstream facet - always show it, same as the Sports
        // Divisions selector. event_tag is the same - always shown, narrowed only by
        // filterOptionsByCurrentGroup at the option level, not hidden as a whole question.
        // Merch tags are organization-wide (no group/division/sport scoping exists for
        // partner-service data), so - like category/event_tag - always shown, never hidden
        // as a whole question.
        if (q.type === 'category' || q.type === 'event_tag' || q.type === 'merch_tag') {
          return true;
        }
        // genre uses the strict facet filter (filterOptionsByCurrentGroup's strict mode - see
        // Mechanism 2 Amendment), which can legitimately leave zero options once the fan has
        // narrowed group/division/sport down to a combination nothing is tagged with (e.g. a
        // sport with no VOD tags at all) - hide the whole question in that case instead of
        // showing its heading over an empty grid.
        if (q.type === 'genre') {
          return this.filterOptionsByCurrentGroup(q.options, true).length > 0;
        }
        // event_type is scoped to one category, and should only appear once that category
        // has been checked - mirrors division_sports only appearing once its Division is
        // checked (heroGroupDivisionSelections).
        if (q.type === 'event_type') {
          const categoryQIndex = (this.allPreferenceQuestions || []).findIndex((cq: any) => cq.type === 'category');
          const selectedCategoryKeys = categoryQIndex >= 0 ? (this.heroGroupPreferenceSelections[categoryQIndex] || []) : [];
          return selectedCategoryKeys.includes(String(q.category_id)) ||
            (this.heroGroupPreferenceSelections[q._qIndex]?.length || 0) > 0;
        }
        // The category picker has no upstream facet - always show it, same as the Sports
        // Divisions selector. Genre and event_tag are the same - always shown, narrowed only
        // by filterOptionsByCurrentGroup at the option level, not hidden as a whole question.
        if (q.type === 'category' || q.type === 'genre' || q.type === 'event_tag' || q.type === 'merch_tag') {
          return true;
        }
        // event_type is scoped to one category, and should only appear once that category
        // has been checked - mirrors division_sports only appearing once its Division is
        // checked (heroGroupDivisionSelections).
        if (q.type === 'event_type') {
          const categoryQIndex = (this.allPreferenceQuestions || []).findIndex((cq: any) => cq.type === 'category');
          const selectedCategoryKeys = categoryQIndex >= 0 ? (this.heroGroupPreferenceSelections[categoryQIndex] || []) : [];
          return selectedCategoryKeys.includes(String(q.category_id)) ||
            (this.heroGroupPreferenceSelections[q._qIndex]?.length || 0) > 0;
        }
        return false;
      });
  }

  // Category/event_type options carry their own is_selected flag straight from the backend -
  // single source of truth (unlike Team/Sport, whose options don't carry that flag, so those
  // still get reconciled against preferable_groups/preferable_sports in
  // applyGroupPreferencesFromResponse). Runs unconditionally on every fetch, independent of
  // whether the fan has any group/sport preference at all.
  seedEventCategorySelectionsFromOptions(): void {
    const updated: Record<number, string[]> = {};
    (this.allPreferenceQuestions || []).forEach((q: any, idx: number) => {
      if (q.type === 'category') {
        const selected = (q.options || []).filter((o: any) => o.is_selected).map((o: any) => String(o.category_id));
        if (selected.length) updated[idx] = selected;
      } else if (q.type === 'event_type') {
        const selected = (q.options || []).filter((o: any) => o.is_selected).map((o: any) => `${q.category_id}::${o.event_type}`);
        if (selected.length) updated[idx] = selected;
      } else if (q.type === 'genre') {
        const selected = (q.options || []).filter((o: any) => o.is_selected).map((o: any) => String(o.tag_id));
        if (selected.length) updated[idx] = selected;
      } else if (q.type === 'event_tag') {
        const selected = (q.options || []).filter((o: any) => o.is_selected).map((o: any) => this.eventTagKey(o));
        if (selected.length) updated[idx] = selected;
      } else if (q.type === 'merch_tag') {
        const selected = (q.options || []).filter((o: any) => o.is_selected).map((o: any) => o.tag_value);
        if (selected.length) updated[idx] = selected;
      }
    });
    if (Object.keys(updated).length) {
      this.heroGroupPreferenceSelections = { ...this.heroGroupPreferenceSelections, ...updated };
    }
  }

  // True once the fan has checked at least one category/event_type option - the Hero panel's
  // Save button needs this in its visibility condition, since heroGroupTypeSelections/
  // heroGroupDivisionSelections (which normally gate that button) don't apply to this axis.
  get hasAnyEventCategorySelection(): boolean {
    return (this.allPreferenceQuestions || []).some((q: any, idx: number) =>
      (q.type === 'category' || q.type === 'event_type' || q.type === 'genre' || q.type === 'event_tag' || q.type === 'merch_tag') &&
      (this.heroGroupPreferenceSelections[idx]?.length || 0) > 0
    );
  }

  trackByQIndex(_: number, q: any): number { return q._qIndex; }

  onEditGroupProfile(): void {
    this.savedHeroGroupTypeSelections = [...this.heroGroupTypeSelections];
    this.savedHeroGroupDivisionSelections = [...this.heroGroupDivisionSelections];
    this.savedHeroGroupPreferenceSelections = { ...this.heroGroupPreferenceSelections };
    this.isEditingGroupProfile = true;
    this.cdr.detectChanges();
  }

  onCancelEditGroupProfile(): void {
    this.heroGroupTypeSelections = [...this.savedHeroGroupTypeSelections];
    this.heroGroupDivisionSelections = [...this.savedHeroGroupDivisionSelections];
    this.heroGroupPreferenceSelections = { ...this.savedHeroGroupPreferenceSelections };
    this.isEditingGroupProfile = false;
    this.refreshVisibleHeroPreferenceQuestions();
    this.cdr.detectChanges();
  }

  // See toggleHeroPreference - clears heroGroupDivisionSelections/heroGroupPreferenceSelections
  // entries for divisions that are no longer in the allowed set after a group selection change.
  private pruneStaleDivisionSelections(): void {
    const allowed = this.getAllowedCategorySportsForSelectedGroups();
    if (!allowed) return; // no group selected - nothing restricted, nothing to prune

    this.heroGroupDivisionSelections = this.heroGroupDivisionSelections.filter(divId => allowed.categoryIds.has(divId));

    const updated = { ...this.heroGroupPreferenceSelections };
    let changed = false;
    (this.allPreferenceQuestions || []).forEach((q: any, idx: number) => {
      if (q.type === 'division_sports' && !allowed.categoryIds.has(Number(q.division_id)) && updated[idx]?.length) {
        delete updated[idx];
        changed = true;
      }
    });
    if (changed) this.heroGroupPreferenceSelections = updated;
  }

  toggleNav(): void {
    this.isNavOpen = !this.isNavOpen;
    this.cdr.detectChanges();
  }

  toggleHub(): void {
    this.isHubOpen = !this.isHubOpen;
    this.cdr.detectChanges();
  }

  isHeroPreferenceSelected(qIndex: number, key: string): boolean {
    return (this.heroGroupPreferenceSelections[qIndex] || []).includes(key);
  }

  toggleHeroPreference(qIndex: number, key: string): void {
    const current = this.heroGroupPreferenceSelections[qIndex] || [];
    const isAdding = !current.includes(key);
    this.heroGroupPreferenceSelections = {
      ...this.heroGroupPreferenceSelections,
      [qIndex]: isAdding ? [...current, key] : current.filter(k => k !== key),
    };

    // Group options are multi-select with no inherent order - track the most recently
    // toggled-on one so it can win as network_group_id in saveHeroGroupingPreferences,
    // instead of whichever group happened to be selected first.
    if (this.allPreferenceQuestions?.[qIndex]?.type === 'group') {
      if (isAdding) {
        this.lastToggledGroupOptionId = Number(key);
      } else if (this.lastToggledGroupOptionId === Number(key)) {
        this.lastToggledGroupOptionId = null;
      }
      // The selected group(s) just changed, which can shrink the allowed division/sport set
      // (e.g. switching to a group with no categories_sports) - drop any division/sport
      // picked under the old selection that's no longer valid, otherwise its question would
      // keep showing even though its division button is now hidden.
      this.pruneStaleDivisionSelections();
      // Same idea for category/event_type/genre - their options are filtered by
      // network_group_ids (filterOptionsByCurrentGroup), which also just changed.
      this.pruneStaleGroupScopedSelections();
      this.refreshVisibleHeroPreferenceQuestions();
      // Merch tags aren't a static membership filter (see field comment) - refresh via a
      // debounced re-fetch instead.
      this.refreshMerchTagOptionsDebounced();
    } else if (this.allPreferenceQuestions?.[qIndex]?.type === 'category') {
      // Checking/unchecking a category changes which event_type question(s) should be
      // visible (see refreshVisibleHeroPreferenceQuestions) - re-filter immediately so
      // unchecking a category also hides its event_type question again.
      if (!isAdding) {
        const updated = { ...this.heroGroupPreferenceSelections };
        let changed = false;
        (this.allPreferenceQuestions || []).forEach((q: any, idx: number) => {
          if (q.type === 'event_type' && String(q.category_id) === key && updated[idx]?.length) {
            delete updated[idx];
            changed = true;
          }
        });
        if (changed) this.heroGroupPreferenceSelections = updated;
      }
      this.refreshVisibleHeroPreferenceQuestions();
    } else if (this.allPreferenceQuestions?.[qIndex]?.type === 'division_sports') {
      // A division/sport pick just changed - genre options can carry network_division_ids/
      // network_sport_ids (see filterOptionsByCurrentGroup) that this affects, same as a group
      // change does for network_group_ids. Re-prune/refresh so a genre no longer valid under
      // the new division/sport selection doesn't stay checked and hidden.
      this.pruneStaleGroupScopedSelections();
      this.refreshVisibleHeroPreferenceQuestions();
    }
  }

  // Debounced (~300ms) refresh of the merch_tag question's options, scoped to whichever groups
  // are currently checked (getSelectedGroupIds - the live in-progress selection, same source
  // filterOptionsByCurrentGroup reads for every other group-scoped type). Called from
  // toggleHeroPreference on every 'group' toggle; a rapid burst of toggles only fires one HTTP
  // call, after the fan pauses.
  //
  // merchTagRefreshToken guards against a slow, superseded response clobbering a newer one: each
  // fired request captures the token before calling, and only applies its result if that token
  // is still the latest when the response arrives (last-request-wins, not last-response-wins).
  // On failure, or when there's no merch_tag question to refresh, this is a silent no-op - a
  // background refresh must never clear or error out options already on screen.
  private refreshMerchTagOptionsDebounced(): void {
    if (this.merchTagRefreshTimer) clearTimeout(this.merchTagRefreshTimer);
    this.merchTagRefreshTimer = setTimeout(() => this.fetchMerchTagOptions(), 300);
  }

  private fetchMerchTagOptions(): void {
    const qIndex = (this.allPreferenceQuestions || []).findIndex((q: any) => q.type === 'merch_tag');
    if (qIndex === -1 || !this.userInfo?.id || !this.org_id) return;

    const token = ++this.merchTagRefreshToken;
    // Starts here, at the actual request - not in refreshMerchTagOptionsDebounced, so the
    // indicator never shows during the debounce wait, only once the call is really in flight.
    this.merchTagOptionsLoading = true;
    const formData = new FormData();
    formData.append('user_id', this.userInfo.id);
    formData.append('organization_id', this.org_id);
    formData.append('group_ids', JSON.stringify([...this.getSelectedGroupIds()]));

    this.appService.postBase1Request(`${API.AI_FAN_AGENT_MERCH_TAG_OPTIONS}?NOLOADER`, formData).subscribe({
      next: (response: any) => {
        if (token !== this.merchTagRefreshToken) return; // superseded - a newer request owns the loading state now
        this.merchTagOptionsLoading = false;
        const options = response?.merch_tag_question?.options;
        if (!options) { this.cdr.detectChanges(); return; } // failed/empty refresh - leave existing options as-is
        this.allPreferenceQuestions = this.allPreferenceQuestions.map((q: any, idx: number) =>
          idx === qIndex ? { ...q, options } : q
        );
        // The template renders visibleHeroPreferenceQuestions, a separately-cached array (see
        // refreshVisibleHeroPreferenceQuestions) - mutating allPreferenceQuestions alone would
        // never reach the screen.
        this.refreshVisibleHeroPreferenceQuestions();
        this.cdr.detectChanges();
      },
      error: () => {
        if (token !== this.merchTagRefreshToken) return; // superseded - a newer request owns the loading state now
        this.merchTagOptionsLoading = false; // existing options untouched - just stop showing the loader
        this.cdr.detectChanges();
      }
    });
  }

  // See toggleHeroPreference - removes category/event_type/genre selections whose option no
  // longer passes filterOptionsByCurrentGroup after a group selection change (same idea as
  // pruneStaleDivisionSelections, but driven by each option's own network_group_ids instead
  // of a group's categories_sports).
  private pruneStaleGroupScopedSelections(): void {
    const updated = { ...this.heroGroupPreferenceSelections };
    let changed = false;

    // Prunes selected keys at `idx` down to whichever still pass the group filter; returns
    // the keys that got dropped (so callers can cascade, e.g. category -> its event_type).
    // `strict` mirrors filterOptionsByCurrentGroup's param - must match the template's call site
    // for the same question type, or a hidden-but-still-selected genre could linger in state.
    const prune = (idx: number, keyOf: (opt: any) => string, options: any[], strict: boolean = false): string[] => {
      const selected = updated[idx];
      if (!selected?.length) return [];
      const visibleKeys = new Set(this.filterOptionsByCurrentGroup(options, strict).map(keyOf));
      const dropped = selected.filter((key: string) => !visibleKeys.has(key));
      if (dropped.length) {
        changed = true;
        const remaining = selected.filter((key: string) => visibleKeys.has(key));
        if (remaining.length) updated[idx] = remaining; else delete updated[idx];
      }
      return dropped;
    };

    const droppedCategoryIds = new Set<string>();
    (this.allPreferenceQuestions || []).forEach((q: any, idx: number) => {
      if (q.type === 'category') {
        prune(idx, (opt: any) => String(opt.category_id), q.options || []).forEach(k => droppedCategoryIds.add(k));
      } else if (q.type === 'genre') {
        prune(idx, (opt: any) => String(opt.tag_id), q.options || [], true);
      } else if (q.type === 'event_tag') {
        prune(idx, (opt: any) => this.eventTagKey(opt), q.options || []);
      }
    });

    // event_type is scoped to one category - if that category just got pruned above, its
    // event_type selections are orphaned regardless of the event_type options' own filter
    // outcome (mirrors the existing uncheck-a-category cascade in toggleHeroPreference).
    (this.allPreferenceQuestions || []).forEach((q: any, idx: number) => {
      if (q.type !== 'event_type') return;
      if (droppedCategoryIds.has(String(q.category_id)) && updated[idx]?.length) {
        delete updated[idx];
        changed = true;
        return;
      }
      prune(idx, (opt: any) => `${q.category_id}::${opt.event_type}`, q.options || []);
    });

    if (changed) this.heroGroupPreferenceSelections = updated;
  }

  saveHeroGroupingPreferences(): void {
    // Sport/division ('division_sports') selection is optional - only group/team selection
    // is required before saving.
    const visibleQuestions = this.visibleHeroPreferenceQuestions;
    for (const q of visibleQuestions) {
      if (q.type !== 'group') continue;
      if (!(this.heroGroupPreferenceSelections[q._qIndex]?.length)) {
        this.toastr.warning(`Please select an answer for: "${q.question}"`);
        return;
      }
    }

    const allGroupSelections: any[] = [];
    const allSportSelections: any[] = [];
    const allDivisionSelections: any[] = [];
    const allEventCategorySelections: any[] = [];
    const categoryOnlySelections: any[] = [];
    const allGenreSelections: any[] = [];
    const allEventTagSelections: any[] = [];
    const allMerchTagSelections: any[] = [];
    let questionText = '';

    // Axes actually editable in this Hero session - passed through so
    // saveBulkGroupingPreferences still sends a now-empty axis (the fan unselected
    // everything under it) instead of silently dropping it and leaving the old
    // saved values untouched in the DB.
    const touchedTypes = new Set<string>();
    visibleQuestions.forEach((q: any) => {
      if (q.type === 'division_sports') { touchedTypes.add('sports'); touchedTypes.add('divisions'); }
      else if (q.type === 'category' || q.type === 'event_type') touchedTypes.add('event_categories');
      else if (q.type === 'genre') touchedTypes.add('genres');
      else if (q.type === 'event_tag') touchedTypes.add('event_tags');
      else if (q.type === 'merch_tag') touchedTypes.add('merch_tags');
    });
    // Groups can't rely on current visibility like the axes above: unchecking a group-type
    // chip (toggleHeroGroupType) clears that question's heroGroupPreferenceSelections entry
    // AND makes the question itself disappear from visibleQuestions (refreshVisibleHeroPreferenceQuestions
    // hides a 'group' question once its group_type chip is off and it has no selections) - so
    // it would never be seen by the loop above, and a fan who fully removed their saved team
    // would have selected_groups silently omitted, leaving the stale team in the DB. Diff
    // against the onEditGroupProfile snapshot instead, across every 'group' question that
    // exists at all (not just the currently visible ones), so a clear-to-empty is still caught.
    (this.allPreferenceQuestions || []).forEach((q: any, idx: number) => {
      if (q.type !== 'group') return;
      const before = this.savedHeroGroupPreferenceSelections[idx] || [];
      const after = this.heroGroupPreferenceSelections[idx] || [];
      if (before.length !== after.length || before.some((k: string) => !after.includes(k))) {
        touchedTypes.add('groups');
      }
    });

    if (this.grouptypeSelectionQuestion?.question) {
      questionText += `**${this.grouptypeSelectionQuestion.question}**\n\n`;
    }

    visibleQuestions.forEach((question: any) => {
      const selectedKeys = this.heroGroupPreferenceSelections[question._qIndex] || [];
      questionText += `**${question.question}**\n`;
      if (question.type === 'group') {
        selectedKeys.forEach((key: string) => {
          const opt = (question.options || []).find((o: any) => String(o.id) === key);
          if (opt) { allGroupSelections.push(opt); questionText += `✓ ${opt.name}\n`; }
        });
      } else if (question.type === 'division_sports') {
        selectedKeys.forEach((key: string) => {
          const [sportId, divId] = key.split('-').map(Number);
          const opt = (question.sport_options || []).find((o: any) => o.sport_id === sportId && o.division_id === divId);
          if (opt) { allSportSelections.push(opt); questionText += `✓ ${opt.sport_name} (${opt.division_name})\n`; }
        });
      } else if (question.type === 'category') {
        // Held separately, not pushed to allEventCategorySelections directly - a category
        // with specific event_types also selected (below) doesn't need its own bare entry,
        // since the specific ones already imply interest in the category (see filter after
        // this forEach).
        selectedKeys.forEach((key: string) => {
          const opt = (question.options || []).find((o: any) => String(o.category_id) === key);
          if (opt) {
            categoryOnlySelections.push({ category_id: opt.category_id, category_name: opt.category_name, event_type: '' });
            questionText += `✓ ${opt.category_name}\n`;
          }
        });
      } else if (question.type === 'event_type') {
        selectedKeys.forEach((key: string) => {
          const eventType = key.slice(key.indexOf('::') + 2);
          allEventCategorySelections.push({ category_id: question.category_id, category_name: question.category_name, event_type: eventType });
          questionText += `✓ ${eventType} (${question.category_name})\n`;
        });
      } else if (question.type === 'genre') {
        selectedKeys.forEach((key: string) => {
          const opt = (question.options || []).find((o: any) => String(o.tag_id) === key);
          if (opt) {
            allGenreSelections.push({ tag_id: opt.tag_id, name: opt.name, tag_category: opt.tag_category });
            questionText += `✓ ${opt.name}\n`;
          }
        });
      } else if (question.type === 'event_tag') {
        selectedKeys.forEach((key: string) => {
          const opt = (question.options || []).find((o: any) => this.eventTagKey(o) === key);
          if (opt) {
            allEventTagSelections.push({
              tag_name: opt.tag_name, genre_id: opt.genre_id,
              category_id: opt.category_id, sub_category_id: opt.sub_category_id
            });
            questionText += `✓ ${opt.tag_name}\n`;
          }
        });
      } else if (question.type === 'merch_tag') {
        selectedKeys.forEach((key: string) => {
          const opt = (question.options || []).find((o: any) => o.tag_value === key);
          if (opt) {
            allMerchTagSelections.push({ tag_value: opt.tag_value, usage_count: opt.usage_count });
            questionText += `✓ ${opt.tag_value}\n`;
          }
        });
      }
      questionText += '\n';
    });

    // Every division picked via the Sports Divisions buttons goes to selected_divisions,
    // independently of selected_sports - picking a specific sport under a division does NOT
    // suppress that division from also being sent here; each axis reflects exactly what was
    // clicked on it.
    const divisionOptions = this.grouptypeSelectionQuestion?.division_options || [];
    this.heroGroupDivisionSelections.forEach((divId: number) => {
      const divOpt = divisionOptions.find((o: any) => Number(o.division_id) === Number(divId));
      if (divOpt) {
        const divLabel = divOpt.label || `Division ${divOpt.division_id}`;
        allDivisionSelections.push({ division_id: divOpt.division_id, division_name: divLabel });
        questionText += `✓ ${divLabel}\n\n`;
      }
    });

    // Only keep a bare category-only entry for categories that have no specific event_type
    // selected - otherwise it's pure duplication (the specific entries already say "interested
    // in this category").
    const categoryIdsWithEventType = new Set(allEventCategorySelections.map((s: any) => s.category_id));
    allEventCategorySelections.push(
      ...categoryOnlySelections.filter((c: any) => !categoryIdsWithEventType.has(c.category_id))
    );

    if (!allGroupSelections.length && !allSportSelections.length && !allDivisionSelections.length && !allEventCategorySelections.length && !allGenreSelections.length && !allEventTagSelections.length && !allMerchTagSelections.length) {
      this.toastr.warning('Please select at least one preference');
      return;
    }

    // Move the most recently toggled-on group to the front - saveBulkGroupingPreferences uses
    // allGroupSelections[0] (via savedGroupNetworkIds[0]) as network_group_id, and without this
    // it would stay whichever group was selected first rather than the latest one.
    if (this.lastToggledGroupOptionId != null) {
      const idx = allGroupSelections.findIndex((g: any) => Number(g.id) === this.lastToggledGroupOptionId);
      if (idx > 0) {
        const [mostRecent] = allGroupSelections.splice(idx, 1);
        allGroupSelections.unshift(mostRecent);
      }
    }

    // Hidden (is_active=false) saved groups are never offered as selectable options, so they
    // can never appear in allGroupSelections - carry forward whatever was already known to be
    // inactive rather than dropping it from this optimistic rebuild (saving preferences never
    // changes which groups a partner has hidden from the network).
    const previouslyInactiveGroupItems = this.savedGroupItems.filter(item => item.is_active === false);
    this.savedGroupItems = [
      ...allGroupSelections.map((g: any) => {
        const gd = this.groupsData?.find((d: any) => Number(d.group_id || d.id) === Number(g.id));
        return { name: g.name, logo: gd?.group_base_logo || '', is_active: true };
      }),
      ...previouslyInactiveGroupItems,
      ...allSportSelections.map((s: any) => ({ name: `${s.sport_name} (${s.division_name})`, logo: '' })),
      ...allDivisionSelections.map((d: any) => ({ name: d.division_name, logo: '' })),
      ...allEventCategorySelections.map((ec: any) => ({
        name: ec.event_type ? `${ec.event_type} (${ec.category_name})` : ec.category_name, logo: ''
      })),
      ...allGenreSelections.map((g: any) => ({ name: g.name, logo: '' })),
      ...allEventTagSelections.map((et: any) => ({ name: et.tag_name, logo: '' })),
      ...allMerchTagSelections.map((mt: any) => ({ name: mt.tag_value, logo: '' })),
    ];

    this.isSavingHeroGroupPreferences = true;
    // All touched axes go in ONE request now - the backend gates each axis on whether its
    // selected_* field is present, not on a single question_type match, so any combination
    // the fan picked at once (groups, sports, divisions, categories, genres, event tags, merch tags) saves together.
    this.saveBulkGroupingPreferences(
      allGroupSelections, allSportSelections, allDivisionSelections, questionText.trim(),
      allEventCategorySelections, allGenreSelections, allEventTagSelections, allMerchTagSelections,
      touchedTypes
    );
    setTimeout(() => { this.isSavingHeroGroupPreferences = false; }, 600);
  }

  // ── Grouping questions flow ────────────────────────────────────────────────

  fetchGroupingQuestions(autoDisplay = false): void {
    if (!this.userInfo?.id || !this.org_id) return;

    const url = `${API.AI_FAN_AGENT_GROUPING_QUESTIONS}?NOLOADER`;
    const formData = new FormData();
    formData.append('user_id', this.userInfo.id);
    formData.append('organization_id', this.org_id);
    formData.append('channel_name', this.channelName || '');
    if (this.performerId) formData.append('performer_id', this.performerId);

    this.appService.postBase1Request(url, formData).subscribe({
      next: (data: any) => {
        this.grouptypeSelectionQuestion = data.grouptype_selection_question || this.grouptypeSelectionQuestion || null;
        // Prefer all_preference_questions (includes group + division_sports).
        // Only fall back to preference_questions if allPreferenceQuestions is still empty —
        // never downgrade a complete set to a partial one.
        if (data.all_preference_questions?.length) {
          this.allPreferenceQuestions = data.all_preference_questions;
        } else if (!this.allPreferenceQuestions.length) {
          this.allPreferenceQuestions = data.preference_questions || [];
        }
        this.groupingQuestions = this.allPreferenceQuestions;
        this.enrichPrefQuestionsWithLogos();
        this.seedEventCategorySelectionsFromOptions();
        this.refreshVisibleHeroPreferenceQuestions();

        // Re-apply saved preferences now that grouptypeSelectionQuestion is available.
        // This corrects any key mismatches and index shifts that occurred when checkAgentPreference
        // responded before this call (grouptypeSelectionQuestion was null at that point).
        if (this.grouptypeSelectionQuestion && this.preferredObj && !this.isEditingGroupProfile &&
          ((this.preferredObj.preferable_groups?.length || 0) > 0 ||
            (this.preferredObj.preferable_sports?.length || 0) > 0)) {
          this.applyGroupPreferencesFromResponse(this.preferredObj);
        }

        // data.status only reflects Team/Sport completion - also trigger for a fan who has
        // only ever answered the category/event_type, genre, or event_tag question
        // (has_event_categories/has_genres/has_event_tags true, status still "new"), so their
        // saved summary shows too.
        if ((data.status === 'complete' || data.has_event_categories || data.has_genres || data.has_event_tags) && !this.isEditingGroupProfile) {
          this.applyGroupPreferencesFromResponse(data);
        }

        if (data.status !== 'complete') {
          if (autoDisplay && this.grouptypeSelectionQuestion) {
            this.displayGroupTypeSelection();
          }
        }
        this.groupInfo = data;

        if (!autoDisplay && this.messagesList.getValue().length > 0 &&
          this.userInfo?.id && this.org_id && this.channelName &&
          data.total_questions > 0 && data.status !== 'complete' &&
          this.grouptypeSelectionQuestion) {
          const groupingDelay = this.preferredObj?.has_org_questions ? 1000 : 1200;
          setTimeout(() => {
            if (this.grouptypeSelectionQuestion) this.displayGroupTypeSelection();
          }, groupingDelay);
        }
      },
      error: () => { }
    });
  }

  displayGroupTypeSelection(): void {
    if (this.groupTypeDisplayed) return;
    this.groupTypeDisplayed = true;
    const containerId = `grouptype-sel-${Date.now()}`;
    const html = this.buildGroupTypeSelectionHtml(this.grouptypeSelectionQuestion, containerId);
    this.messagesList.next([...this.messagesList.getValue(), {
      message: html,
      time: moment().format('hh:mm A'),
      sender: 'bot',
      isStreaming: false,
      hideActions: true,
    }]);
    this.disableSend = false;
    this.cdr.detectChanges();
    setTimeout(() => this.scrollToBottomImmediate(), 300);
  }

  private escapeHtml(value: any): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  buildGroupTypeSelectionHtml(question: any, containerId: string): string {
    const bgColor = this.settingsData?.ai_agent_accent_color || '#000';
    const fontColor = this.settingsData?.ai_agent_text_color || '#ffffff';
    let html = `<div class="grouping-question-container" id="grouptype-container-${containerId}">
        <p class="mb-10"><strong>${this.escapeHtml(question.question)}</strong></p>`;
    if (question.description) {
      html += `<p style="color:#666;margin-bottom:12px;">${this.escapeHtml(question.description)}</p>`;
    }
    if (question.group_type_options?.length) {
      html += `<h4 style="margin:10px 0 8px;">Your Interests:</h4>
          <div class="grouping-options-list" id="grouptype-opts-${containerId}">`;
      question.group_type_options.forEach((option: any) => {
        html += `<label class="grouping-option-item">
            <input type="checkbox" value="${this.escapeHtml(option.value)}" data-label="${this.escapeHtml(option.label)}"
                   onchange="window.onGroupTypeChange('${containerId}')">
            <span>${this.escapeHtml(option.label)} (${this.escapeHtml(option.count)})</span>
          </label>`;
      });
      html += `</div>`;
    }
    if (question.division_options?.length) {
      html += `<h4 style="margin:10px 0 8px;">Sports Divisions:</h4>
          <div class="grouping-options-list" id="division-opts-${containerId}">`;
      question.division_options.forEach((option: any) => {
        html += `<label class="grouping-option-item">
            <input type="checkbox" value="${this.escapeHtml(option.division_id)}" data-label="${this.escapeHtml(option.label)}"
                   onchange="window.onGroupTypeChange('${containerId}')">
            <span>${this.escapeHtml(option.label)}</span>
          </label>`;
      });
      html += `</div>`;
    }
    html += `<div id="preference-questions-${containerId}"></div>
        <div class="grouping-question-actions mt-10" id="save-actions-${containerId}" style="display:none;">
          <button class="save-grouping-btn"
                  onclick="window.saveAllGroupingPreferences('${containerId}')"
                  style="background-color:${bgColor};color:${this.escapeHtml(fontColor)}">Save All Preferences</button>
        </div>
      </div>`;
    return html;
  }

  saveBulkGroupingPreferences(
    groupSelections: any[], sportSelections: any[], divisionSelections: any[] = [], questionText = '',
    eventCategorySelections: any[] = [], genreSelections: any[] = [], eventTagSelections: any[] = [],
    merchTagSelections: any[] = [],
    // Axes whose question was shown/editable in this save session - lets an axis that got
    // cleared down to zero still be sent (as an empty list) instead of being dropped, which
    // would otherwise leave the fan's old saved values for that axis untouched in the DB.
    touchedTypes: Set<string> = new Set()
  ): void {
    const url = `${API.AI_FAN_AGENT_SAVE_GROUPING}?NOLOADER`;
    const formData = new FormData();
    formData.append('user_id', this.userInfo.id);
    formData.append('organization_id', this.org_id);
    formData.append('channel_name', this.channelName || '');
    // question_type is descriptive only now (backend gates on which selected_* fields are
    // actually present) - keep the existing group/sport/both values when that's all that's
    // touched (unchanged behavior for the chat-embedded grouptype-selection flow, which never
    // sends the other three), and use 'bulk' once any of the newer axes are involved too.
    if (eventCategorySelections.length || genreSelections.length || eventTagSelections.length || merchTagSelections.length) {
      formData.append('question_type', 'bulk');
    } else if (groupSelections.length > 0 && (sportSelections.length > 0 || divisionSelections.length > 0)) {
      formData.append('question_type', 'both');
    } else if (groupSelections.length > 0) {
      formData.append('question_type', 'group');
    } else {
      formData.append('question_type', 'division_sports');
    }
    formData.append('question_text', questionText || 'Bulk preferences selection');
    // Sports (a specific sport picked within a division) and divisions (a bare division with
    // no specific sport picked) are sent as two separate keys. Division context for a sport
    // is already carried by selected_divisions when that division was also picked, so
    // division_id/division_name are stripped out of each selected_sports entry here rather
    // than duplicated - sportSelections itself keeps them (used for display, e.g. savedGroupItems).
    if (groupSelections.length > 0 || touchedTypes.has('groups')) formData.append('selected_groups', JSON.stringify(groupSelections));
    if (sportSelections.length > 0 || touchedTypes.has('sports')) {
      const sportsPayload = sportSelections.map(({ division_id, division_name, ...rest }: any) => rest);
      formData.append('selected_sports', JSON.stringify(sportsPayload));
    }
    if (divisionSelections.length > 0 || touchedTypes.has('divisions')) formData.append('selected_divisions', JSON.stringify(divisionSelections));
    if (eventCategorySelections.length > 0 || touchedTypes.has('event_categories')) formData.append('selected_event_categories', JSON.stringify(eventCategorySelections));
    if (genreSelections.length > 0 || touchedTypes.has('genres')) formData.append('selected_genres', JSON.stringify(genreSelections));
    if (eventTagSelections.length > 0 || touchedTypes.has('event_tags')) formData.append('selected_event_tags', JSON.stringify(eventTagSelections));
    if (merchTagSelections.length > 0 || touchedTypes.has('merch_tags')) formData.append('selected_merch_tags', JSON.stringify(merchTagSelections));

    this.appService.postBase1Request(url, formData).subscribe({
      next: (result: any) => {
        if (result?.success) {
          if (!result.has_preferences || result.status !== 'complete') {
            setTimeout(() => this.fetchGroupingQuestions(true), 300);
          } else {
            this.groupProfileSaved = true;
            this.isEditingGroupProfile = false;
            this.groupInfo = { ...this.groupInfo, status: 'complete' };
            // See saveHeroGroupingPreferences' identical comment: hidden saved groups are
            // never selectable, so carry forward whatever was already known to be inactive
            // rather than losing it from this optimistic rebuild.
            const previouslyInactiveGroupItems = this.savedGroupItems.filter(item => item.is_active === false);
            this.savedGroupItems = [
              ...groupSelections.map((g: any) => {
                const gd = this.groupsData?.find((d: any) => Number(d.group_id || d.id) === Number(g.id));
                return { name: g.name, logo: gd?.group_base_logo || '', is_active: true };
              }),
              ...previouslyInactiveGroupItems,
              ...sportSelections.map((s: any) => ({ name: `${s.sport_name} (${s.division_name})`, logo: '' })),
              ...divisionSelections.map((d: any) => ({ name: d.division_name, logo: '' })),
              ...eventCategorySelections.map((ec: any) => ({
                name: ec.event_type ? `${ec.event_type} (${ec.category_name})` : ec.category_name, logo: ''
              })),
              ...genreSelections.map((g: any) => ({ name: g.name, logo: '' })),
              ...eventTagSelections.map((et: any) => ({ name: et.tag_name, logo: '' })),
              ...merchTagSelections.map((mt: any) => ({ name: mt.tag_value, logo: '' })),
            ];
            this.savedGroupNetworkIds = groupSelections.map((g: any) => Number(g.id)).filter(Boolean);
            this.loadDashboard();
            this.toastr.success('Your preferences have been saved!');
            this.preferencesDialogRef?.close();
            this.cdr.detectChanges();
          }
        } else {
          this.toastr.error('Failed to save preferences. Please try again.');
        }
      },
      error: () => { this.toastr.error('Failed to save preferences. Please try again.'); }
    });
  }

  // ── Channel profile selection ──────────────────────────────────────────────

  isSubChannelSelected(channel: any): boolean {
    return this.selectedSubChannels.some(c => c.channel_id === channel.channel_id);
  }

  onEditChannelProfile(): void {
    this.savedSelectedSubChannels = [...this.selectedSubChannels];
    this.isEditingChannelProfile = true;
    this.cdr.detectChanges();
  }

  onCancelEditChannelProfile(): void {
    this.selectedSubChannels = [...this.savedSelectedSubChannels];
    this.channelProfileSaved = this.selectedSubChannels.length > 0;
    this.isEditingChannelProfile = false;
    this.cdr.detectChanges();
  }

  // Single Edit affordance for both sections - whichever section's Edit/Close button is
  // visible, it reopens (or re-collapses) channels and groups/divisions together, so a
  // section that's currently collapsed (nothing picked there) still comes back for editing.
  onEditAllPreferences(): void {
    this.onEditChannelProfile();
    this.onEditGroupProfile();
  }

  onCancelEditAllPreferences(): void {
    this.onCancelEditChannelProfile();
    this.onCancelEditGroupProfile();
  }

  toggleSubChannelSelection(channel: any): void {
    if (this.isSubChannelSelected(channel)) {
      this.selectedSubChannels = this.selectedSubChannels.filter(c => c.channel_id !== channel.channel_id);
    } else {
      this.selectedSubChannels = [...this.selectedSubChannels, channel];
    }
    this.channelProfileSaved = false;
    this.isEditingChannelProfile = true;
  }

  saveChannelProfile(): void {
    if (!this.userInfo?.id || !this.org_id || this.isSavingChannelProfile) return;
    this.isSavingChannelProfile = true;
    const fd = new FormData();
    fd.append('user_id', this.userInfo.id);
    fd.append('organization_id', String(this.org_id));
    this.selectedSubChannels.forEach(ch => {
      fd.append('preferred_channels', ch.channel_name);
      fd.append('preferred_channels_id', ch.channel_id);
      fd.append('preferred_channels_display_names', ch.channel_display_name || ch.channel_name);
    });
    this.appService.postBase1Request(`${API.AI_FAN_AGENT}/save-preferred-channels?NOLOADER`, fd).subscribe({
      next: () => {
        this.isSavingChannelProfile = false;
        this.channelProfileSaved = true;
        this.hasPreferredChannel = true;
        this.savedPreferredChannelIds = this.selectedSubChannels.map(c => Number(c.channel_id));
        this.isEditingChannelProfile = false;
        const selectedChannelId = this.selectedSubChannels.length > 0 ? Number(this.selectedSubChannels[0].channel_id) : 0;
        this.getChannelHome(selectedChannelId);
        this.loadDashboard();
        this.toastr.success('Your preferences have been saved!');
        this.preferencesDialogRef?.close();
      },
      error: () => { this.isSavingChannelProfile = false; }
    });
  }

  // ── Sub-channel selection UI ───────────────────────────────────────────────

  setSubChannelsData(): void {
    if (!this.subChannels?.length) return;
    const bgColor = this.settingsData?.ai_agent_accent_color || '#000';
    const fontColor = this.settingsData?.ai_agent_text_color || '#ffffff';
    const channelsHtml = this.subChannels.map((channel: any) =>
      `<label class="radio-option">
          <input type="radio" name="subchannel" value="${channel.channel_id}" style="accent-color:${bgColor};">
          <span>${channel.channel_display_name || channel.channel_name}</span>
        </label>`
    ).join('');
    const subchannelOptions = `<div class="subchannel-selection mt-15">
          <p class="mb-10"><strong>Which team do you want to follow?</strong></p>
          <div class="radio-group">${channelsHtml}</div>
        </div>
        <div class="personalization-actions mt-15">
          <button class="save-preferences-btn"
            onclick="window.saveSubchannelSelection(document.querySelector('input[name=subchannel]:checked').value)"
            style="margin-left:15px;color:${fontColor};background-color:${bgColor}">Save</button>
        </div>`;
    this.messagesList.next([...this.messagesList.getValue(), {
      message: subchannelOptions,
      time: moment().format('hh:mm A'),
      sender: 'bot',
      isStreaming: false,
      hideActions: true,
    }]);
    setTimeout(() => this.scrollToBottomImmediate(), 150);
  }

  getPersonalizeMsg(): void {
    if (this.personalizeMessageDisplayed) return;
    const personalizationMessage = this.preferredObj?.response;
    if (!personalizationMessage) return;
    this.personalizeMessageDisplayed = true;
    this.messagesList.next([...this.messagesList.getValue(), {
      message: personalizationMessage,
      time: moment().format('hh:mm A'),
      sender: 'bot',
      isStreaming: false,
      hideActions: true,
    }]);
    setTimeout(() => this.scrollToBottomImmediate(), 150);
  }

  // ── Initial greeting ───────────────────────────────────────────────────────

  private sendInitialGreeting(): void {
    if (this.threadId) {
      // Reopening after minimize keeps the live conversation; reloading it would re-add the intro
      // greeting and swap the streamed replies for the stored copies.
      if (!this.messagesList.getValue().length) this.loadChatHistory({ id: this.threadId });
      return;
    }
    if (this.initialMessageSent) return;
    this.initialMessageSent = true;

    this.startLoadingAnimation();

    setTimeout(() => {
      this.stopLoadingAnimation();
      this.isFirstBotResponse = false;

      this.disableSend = false;
      if (this.preferredObj) {
        if (this.currentGroupId ? this.preferredObj.response : this.preferredObj.has_org_questions) {
          setTimeout(() => this.getPersonalizeMsg(), 500);
        }
      } else if (!this.preferencesCheckInFlight) {
        setTimeout(() => this.checkAgentPreference(true), 500);
      }
      // if preferencesCheckInFlight: early call in flight; its handler checks initialMessageSent to show message
    }, 4000);
  }

  getchannelInitialMessage(channelName: string): string {
    // Dynamic Agent Name (specs/dynamic-agent-name): when the network has a
    // configured agent_name, introduce it by that name ("I'm Freddy") instead
    // of the generic "I'm your {channelName} Fan Agent".
    const agentIntro = this.displayAgentName
      ? `I'm <span style="font-weight:bold;">${this.displayAgentName}</span>`
      : `I'm your <span style="font-weight:bold;">${channelName} Fan Agent</span>`;

    if (this.userInfo?.id) {
      return `
          <p>Hello! ${agentIntro}, here to assist you.</p>
          <p>By personalizing your experience, I can tailor content specifically to your interests, including:</p>
          <ul>
            <li>Network-specific events and schedules</li>
            <li>Exclusive merchandise drops and special offers</li>
            <li>Access to community spaces and sub-channels</li>
            <li>Smarter recommendations based on your favorite teams and players</li>
          </ul>
        `;
    }
    return `
        <p>Hi there! ${agentIntro}.</p>
        <ul>
          <li>Network-specific events and merchandise</li>
          <li>Network-specific guidance and support</li>
        </ul>
        <p>How can I assist you today?</p>
        <p><span class="signin-trigger" style="color:var(--fa-accent, #eba921);font-weight:bold;cursor:pointer;">Sign in</span> for a better experience.</p>
      `;
  }

  getGlobalInitialMessage(): string {
    return `
        <p>Hi there! I'm your BigBeeAI Fan Agent. I'm here to help you explore everything happening across the platform.</p>
        <p>I can help with:</p>
        <ul>
          <li>Events: discover activities, matches, shows, and programs</li>
          <li>Merchandise: browse products, pricing, and availability</li>
          <li>Communities: join and engage with fan spaces</li>
          <li>Networks: navigate connected spaces with posts, media, and follows</li>
        </ul>
        <p>How can I assist you today?</p>
      `;
  }

  // ── Dashboard data ──────────────────────────────────────────────────────────

  private loadDashboard(): void {
    const orgId = this.org_id || this.settingsData?.organization_id;
    if (!orgId) { this.isDashboardLoading = false; return; }

    this.isDashboardLoading = true;
    this.recommendedItems = [];
    this.nextHubEvents = [];
    this.ureportNews = [];
    this.fetchRecommendedForYou(orgId);
  }

  // Every call bumps dashboardCallId itself (not just loadDashboard) so that whichever call
  // was issued most recently always wins, even when a preference/group update fires a second
  // fetch while the first one (issued before preferences were known) is still in flight — a
  // late-arriving, unfiltered response can no longer clobber a properly-filtered later one.
  private fetchRecommendedForYou(orgId: any): void {
    const formData = new FormData();
    formData.append('user_id', this.userInfo?.id ?? '');
    formData.append('organization_id', orgId ?? '');
    formData.append('channel_name', this.channelName || '');
    // The routed group wins (you're on that group's page), else the saved preferred group so
    // "All" returns the preferred group's content server-side; omitted = network-wide default.
    const preferredGroupId = this.currentGroupId || this.savedGroupNetworkIds?.[0] || 0;
    if (preferredGroupId) {
      formData.append('network_group_id', preferredGroupId);
    }
    formData.append('community_spaces_limit', '1');

    const callId = ++this.dashboardCallId;
    const url = `${API.AI_RECOMMENDED_FOR_YOU}?NOLOADER`;
    this.appService.postBaseRequest(url, formData).subscribe({
      next: (response: any) => {
        if (callId !== this.dashboardCallId) return;
        this.lastRecommendedForYouResponse = response;
        this.applyRecommendedForYouResponse(response);
        this.isDashboardLoading = false;
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error('[AI Agent][Recommended For You] API request failed', error);
        if (callId === this.dashboardCallId) this.isDashboardLoading = false;
      }
    });
  }

  // Old flow re-filtered already-fetched data locally on preference change (reFilterEventsByChannels/
  // reFilterTrendingByGroups) rather than re-hitting the network, only refetching on a genuine
  // context change (group/channel switch, explicit save). Mirrors that here: re-run the same
  // filter/mapping pass against the cached raw response instead of firing a second overlapping
  // request that could race with the one already in flight.
  private reapplyCachedRecommendations(): void {
    if (!this.lastRecommendedForYouResponse) return;
    this.applyRecommendedForYouResponse(this.lastRecommendedForYouResponse);
    this.cdr.detectChanges();
  }

  private filterByGroupPreference(items: any[], withFallback = true): any[] {
    if (!items?.length) return items || [];
    const hasGroupIds = this.savedGroupNetworkIds.length > 0;
    // As of this writing AI_RECOMMENDED_FOR_YOU items only carry channel_name, not channel_id
    // (confirmed against the live API) - prefer channel_id when an item has one (more reliable
    // than a normalized name match), and fall back to channel_name otherwise so this keeps
    // working today and upgrades automatically once the API starts returning channel_id.
    const preferredChannelIds = this.selectedSubChannels.map((c: any) => Number(c.channel_id)).filter(Boolean);
    const preferredChannelNames = this.selectedSubChannels
      .map((c: any) => (c.channel_name || '').toLowerCase().trim())
      .filter(Boolean);
    const hasChannelFilter = preferredChannelIds.length > 0 || preferredChannelNames.length > 0;

    if (!hasGroupIds && !hasChannelFilter) return items;

    const filtered = items.filter((item: any) => {
      if (hasGroupIds && this.savedGroupNetworkIds.includes(Number(item.network_group_id))) return true;
      if (item.channel_id != null && preferredChannelIds.includes(Number(item.channel_id))) return true;
      if (preferredChannelNames.includes((item.channel_name || '').toLowerCase().trim())) return true;
      return false;
    });
    if (filtered.length || !withFallback) return filtered;
    return items;
  }

  // Maps AI_RECOMMENDED_FOR_YOU's four category arrays onto the same card shape the
  // "Recommended For You" grid already renders (badge/title/subtitle/imageUrl/ctaLabel/category),
  // taking the top result per category. `live_events` wins over `events` for the event slot
  // since a live event is always more relevant than an upcoming one.
  private applyRecommendedForYouResponse(response: any): void {
    const rawLiveEvents = response?.live_events || [];
    const rawUpcomingEvents = response?.events || [];
    // Try a preference match across live+upcoming together first - if either has one, use
    // only the matched subsets (even if that leaves the other empty), so a preference-matched
    // upcoming event isn't outranked by an unrelated live event that only survived because its
    // own array had no match to fall back from. Only fall back to the raw arrays when neither
    // has a preference match at all.
    const strictLiveEvents = this.filterByGroupPreference(rawLiveEvents, false);
    const strictUpcomingEvents = this.filterByGroupPreference(rawUpcomingEvents, false);
    const hasPreferenceMatch = strictLiveEvents.length > 0 || strictUpcomingEvents.length > 0;
    const liveEvents = hasPreferenceMatch ? strictLiveEvents : rawLiveEvents;
    const upcomingEvents = hasPreferenceMatch ? strictUpcomingEvents : rawUpcomingEvents;
    const liveEvent = liveEvents[0];
    const upcomingEvent = upcomingEvents[0];
    this.aiRecommendedEvent = liveEvent
      ? this.mapAiEventItem(liveEvent, true)
      : upcomingEvent ? this.mapAiEventItem(upcomingEvent, false) : null;

    const contentItem = this.filterByGroupPreference(response?.content || [])[0];
    this.aiRecommendedContent = contentItem ? this.mapAiContentItem(contentItem) : null;

    const merchItem = this.filterByGroupPreference(response?.products || [])[0];
    this.aiRecommendedMerch = merchItem ? this.mapAiMerchItem(merchItem) : null;

    const communityItem = this.filterByGroupPreference(response?.community || [])[0];
    this.aiRecommendedCommunity = communityItem ? this.mapAiCommunityItem(communityItem) : null;

    this.ureportNews = toFanNewsStories(response?.ureport_news);

    this.buildRecommendItems();
    this.buildNextHubEventsFromResponse(liveEvents, upcomingEvents);
  }

  // Center carousel now comes from the same AI_RECOMMENDED_FOR_YOU response instead of a
  // separate banner API call - combine live_events + events, live events always first.
  // Reuses the already-filtered arrays from applyRecommendedForYouResponse so the carousel
  // and the grid card agree on which events match the saved group/channel preference.
  private buildNextHubEventsFromResponse(liveEvents: any[], upcomingEvents: any[]): void {
    const tzMatch = /\((.*)\)/.exec(new Date().toString());
    const tzName = tzMatch?.[1] || '';
    const tzAbbr = tzName.includes(' ') ? tzName.split(' ').map((w: string) => w[0]).join('') : tzName;

    this.nextHubEvents = [
      ...liveEvents.map((e: any) => this.mapAiCarouselEvent(e, true, tzAbbr)),
      ...upcomingEvents.map((e: any) => this.mapAiCarouselEvent(e, false, tzAbbr)),
    ].slice(0, 10);

    this.centerCarouselIndex = 0;
    if (this.nextHubEvents.length > 0) { this.startCountdown(); this.startAutoScroll(); }
  }

  private mapAiCarouselEvent(item: any, isLive: boolean, tzAbbr: string): any {
    const startRaw = item.event_start_date_time || item.event_start_time;
    // parseEventDate returns null (not a fabricated "now") when the API gives us a missing/
    // unparseable date - see its own comment for why defaulting to "now" here caused the
    // displayed date/time to drift forward every time this mapping re-ran for such an event.
    const startLocal = this.parseEventDate(startRaw);
    return {
      title: item.stream_video_title || '',
      description: (item.stream_video_description && item.stream_video_description !== 'null')
        ? item.stream_video_description
        : '',
      teamLogo: item.event_carousel_banner || item.event_home_banner || '',
      date: startLocal ? (moment as any)(startLocal).format('ddd, MMM D, YYYY') : '',
      time: startLocal ? ((moment as any)(startLocal).format('h:mm A') + (tzAbbr ? ' ' + tzAbbr : '')) : '',
      location: '',
      homeTeamName: item.match_info?.home_team_name || '',
      awayTeamName: item.match_info?.away_team_name || '',
      homeTeamImage: item.match_info?.home_team_image || '',
      awayTeamImage: item.match_info?.away_team_image || '',
      divisionName: item.match_info?.division_name || '',
      sportName: item.match_info?.sport_name || '',
      days: '00', hrs: '00', min: '00', sec: '00',
      isLive,
      slug: item.slug || '',
      channelName: item.channel_name || this.channelName,
      groupSlug: this.getGroupSlugForItem(item),
      eventDate: startLocal,
      ticketKey: item.ticket_key || '',
      selectedType: item.selected_type || '',
      ticketPricingType: item.ticket_pricing_type || '',
      streamid: item.streamid ?? '',
      networkGroupId: item.network_group_id,
      ctaLabel: this.getEventCtaLabel(item.ticket_key, item.selected_type, isLive, item.ticket_pricing_type),
    };
  }

  // A present ticket_key means the user already holds a ticket - the CTA should send them
  // to the stream/schedule instead of asking them to buy a ticket again.
  // ticket_pricing_type can be a combination (e.g. "free_and_paid"), so any paid option means tickets.
  // Free events: in the v2 design a logged-in fan registers in one click ("Remind Me", US-20);
  // guests and the v1 design get the form.
  private getEventCtaLabel(ticketKey: any, selectedType: any, isLive: boolean, pricingType?: any): string {
    if (ticketKey) {
      return selectedType === 'single_session_event' ? 'Watch' : 'Watch';
    }
    const pricing = String(pricingType || '').toLowerCase();
    if (pricing.includes('paid')) return 'Get Tickets';
    if (pricing === 'free') return this.remindMeEnabled && this.userInfo?.id ? REMIND_ME_LABEL : 'REGISTER TO WATCH';
    return isLive ? 'Watch Live' : 'Get Tickets';
  }

  private mapAiEventItem(item: any, isLive: boolean): any {
    return {
      badge: isLive ? 'LIVE' : 'UPCOMING',
      title: item.stream_video_title || '',
      // Description, not the date/time - stream_video_description sometimes comes back as
      // the literal string "null" rather than a real null/empty value, so treat that as missing.
      subtitle: (item.stream_video_description && item.stream_video_description !== 'null')
        ? item.stream_video_description
        : '',
      imageUrl: item.event_home_banner || '',
      ctaLabel: this.getEventCtaLabel(item.ticket_key, item.selected_type, isLive, item.ticket_pricing_type),
      category: 'event',
      channelName: item.channel_name || this.channelName,
      ticketKey: item.ticket_key || '',
      selectedType: item.selected_type || '',
      ticketPricingType: item.ticket_pricing_type || '',
      streamid: item.streamid ?? '',
      eventId: item.streamid ?? '',
      slug: item.slug || '',
      groupSlug: this.getGroupSlugForItem(item),
      networkGroupId: item.network_group_id,
      sportName: item.match_info?.sport_name || '',
    };
  }

  private mapAiContentItem(item: any): any {
    const badge = item.vod_video_audio ||  (item.genre && item.genre[0]) || 'CONTENT';
    return {
      badge,
      title: item.stream_video_title || '',
      subtitle: item.stream_video_description || '',
      imageUrl: item.video_thumbnail_image || this.getVodFallbackImage(item),
      ctaLabel: 'Play Now',
      category: 'vod',
      tag_category: badge,
      slug: item.slug || '',
      channelName: item.channel_name || this.channelName,
      content_id: item.streamid ?? '',
      groupSlug: this.getGroupSlugForItem(item),
      networkGroupId: item.network_group_id,
      genres: item.genre || [],
    };
  }

  private mapAiMerchItem(item: any): any {
    return {
      badge: 'MERCH DROP',
      title: item.name || '',
      subtitle: item.short_description || '',
      imageUrl: (item.media_urls && item.media_urls[0]) || '',
      ctaLabel: 'Shop Now',
      category: 'merchandise',
      channelName: this.channelName,
      collectionId: item.collection_ids,
      productId: item.product_id,
      groupSlug: this.getGroupSlugForItem(item),
      networkGroupId: item.network_group_id,
    };
  }

  private mapAiCommunityItem(item: any): any {
    return {
      badge: 'COMMUNITY',
      title: item.channel_name || '',
      // Matches the old normalizeTrendingSpaces subtitle exactly - member count over description.
      subtitle: item.members > 0 ? `${item.members} Members` : 'Trending Space',
      imageUrl: this.getCommunityLogo(item),
      ctaLabel: 'Join Now',
      category: 'community',
      channelId: item.space_id || '',
      groupSlug: this.getGroupSlugForItem(item),
      networkGroupId: item.network_group_id,
    };
  }

  // Root-cause fix: this used to fall back to `new Date()` ("right now") whenever `raw` was
  // missing/unparseable. Since mapAiCarouselEvent can re-run for the same event (e.g. via
  // reapplyCachedRecommendations() on a preference/channel change), each re-run fabricated a
  // fresh "now" for that event - making its displayed date/time (and the countdown, which
  // reads off the same value) appear to drift forward instead of showing a fixed date, or
  // reporting the event as already-live immediately. Returning null instead lets callers
  // treat "we don't actually know this event's start time" as its own state, rather than
  // silently substituting the current moment.
  private parseEventDate(raw: string): Date | null {
    if (!raw) return null;
    // Backend event timestamps come back as a bare "YYYY-MM-DD HH:mm:ss" with no timezone
    // marker but are actually UTC (see event-details.component.ts's identical handling of
    // publish_date_time via moment.utc(..., "YYYY-MM-DD HH:mm:ss").tz(zone)). Native `new
    // Date()` parses that exact shape as LOCAL time, not UTC, so the carousel was showing
    // the raw UTC clock value as if it were already the viewer's local time instead of
    // converting it - parse this shape as UTC explicitly, before the native Date fallback.
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)) {
      const utcParsed = (moment as any).utc(raw, 'YYYY-MM-DD HH:mm:ss');
      if (utcParsed.isValid()) return utcParsed.tz(moment.tz.guess()).toDate();
    }
    // ISO / standard formats with an explicit timezone marker — native Date handles UTC offset correctly
    const d = new Date(raw);
    if (!isNaN(d.getTime())) return d;
    // Pre-formatted strings like "SAT, AUG 3, 2025 11:00 AM PT"
    // moment with explicit formats; non-strict so trailing timezone label is ignored
    const m = (moment as any)(raw, [
      'ddd, MMM D, YYYY h:mm A',
      'ddd, MMM D, YYYY',
      'MMM D, YYYY h:mm A',
      'MMM D, YYYY',
      'DD MMM YYYY, hh:mm A',
      'DD MMM YYYY',
      'YYYY-MM-DD HH:mm:ss',
    ]);
    return m.isValid() ? m.toDate() : null;
  }

  private startCountdown(): void {
    clearInterval(this.countdownInterval);
    const tick = () => {
      let allExpired = true;
      this.nextHubEvents.forEach((event: any) => {
        if (!event.eventDate) return;
        const rawDiffMs = event.eventDate.getTime() - Date.now();
        // Flip to LIVE the moment the event's start time is reached, instead of leaving
        // isLive at whatever the API returned when the carousel was built - previously
        // nothing ever set this, so the countdown box (*ngIf="!event.isLive" in the
        // template) stayed stuck showing "EVENT STARTS IN 00 00 00 00" indefinitely.
        if (rawDiffMs <= 0 && !event.isLive) {
          event.isLive = true;
          event.ctaLabel = this.getEventCtaLabel(event.ticketKey, event.selectedType, true, event.ticketPricingType);
        }
        const diffMs = Math.max(0, rawDiffMs);
        const totalSecs = Math.floor(diffMs / 1000);
        const totalMins = Math.floor(totalSecs / 60);
        event.days = Math.floor(totalMins / 1440).toString().padStart(2, '0');
        event.hrs = (Math.floor(totalMins / 60) % 24).toString().padStart(2, '0');
        event.min = (totalMins % 60).toString().padStart(2, '0');
        event.sec = (totalSecs % 60).toString().padStart(2, '0');
        if (diffMs > 0) allExpired = false;
      });
      this.cdr.detectChanges();
      if (allExpired && this.nextHubEvents.length > 0) {
        clearInterval(this.countdownInterval);
      }
    };
    tick();
    this.countdownInterval = setInterval(tick, 1000);
  }

  centerCarouselPrev(): void {
    this.centerCarouselIndex = this.centerCarouselIndex > 0
      ? this.centerCarouselIndex - 1
      : this.nextHubEvents.length - 1;
  }

  centerCarouselNext(): void {
    this.centerCarouselIndex = this.centerCarouselIndex < this.nextHubEvents.length - 1
      ? this.centerCarouselIndex + 1
      : 0;
  }

  openUrl(url: string): void {
    if (url) window.open(url, '_blank');
  }

  autoScrollInterval: any;

  startAutoScroll(): void {
    this.stopAutoScroll();
    this.autoScrollInterval = setInterval(() => {
      this.ngZone.run(() => {
        this.centerCarouselNext();
        this.cdr.detectChanges();
      });
    }, 5000);
  }

  stopAutoScroll(): void {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
    }
  }


  navigateToCommunitySpace(channelId: string): void {
    this.isExpanded = false;
    this.skipAutoExpand = true;
    setTimeout(() => { this.skipAutoExpand = false; }, 5000);
    setTimeout(() => {
      const base = this.commanSubscribeService.getChannelBasePath(this.channelName) + '/community';
      this.router.navigate([channelId ? base + '/' + channelId : base]);
    }, 200);
  }

  goToSubscribe(): void {
    this.isExpanded = false;
    this.skipAutoExpand = true;
    setTimeout(() => { this.skipAutoExpand = false; }, 5000);
    setTimeout(() => {
      this.router.navigate([this.commanSubscribeService.getChannelBasePath(this.channelName) + '/subscription-list']);
      this.commanSubscribeService.getWebPageSettings(0);
    }, 200);
  }

  // v2 carousel CTA: "Remind Me" registers in place, anything else keeps the existing navigation.
  isRemindMe(item: any): boolean {
    return this.remindMeEnabled && item?.ctaLabel === REMIND_ME_LABEL && !!this.userInfo?.id;
  }

  // One-click free registration (US-20) behind the app loader. On success the card turns "Watch"
  // from the returned ticket_key and the recommendations are re-fetched quietly, so every card
  // shows its latest label without reloading the app. Events that need the full form (questions,
  // shipping, no free ticket) open the event page as before.
  async remindMe(item: any): Promise<void> {
    if (item.isRegistering) return;
    item.isRegistering = true;
    this.loader.start();
    try {
      const result = await this.quickRegister.register(item.slug, this.userInfo, item.networkGroupId);
      this.loader.stop();
      item.isRegistering = false;
      if (result.status === 'needs-form') {
        // Carousel events carry isLive instead of a badge
        this.navigateToItem(item.badge ? item : { ...item, badge: item.isLive ? 'LIVE' : 'UPCOMING' });
        return;
      }
      this.toastr.success(`You're registered for ${item.title || 'this event'}`);
      this.markRegistered(item, result.ticketKey);
      this.refreshRecommendations();
    } catch (error: any) {
      this.loader.stop();
      item.isRegistering = false;
      this.toastr.error(error?.message || 'Could not register, please try again');
    }
    this.cdr.detectChanges();
  }

  // Already-registered fans get no new ticket_key; the re-fetch brings theirs.
  private markRegistered(item: any, ticketKey: string): void {
    if (!ticketKey) return;
    item.ticketKey = ticketKey;
    item.ctaLabel = this.getEventCtaLabel(ticketKey, item.selectedType, !!item.isLive, item.ticketPricingType);
  }

  // Same request as the dashboard load, without clearing the cards (no skeleton).
  protected refreshRecommendations(): void {
    const orgId = this.org_id || this.settingsData?.organization_id;
    if (orgId) this.fetchRecommendedForYou(orgId);
  }

  navigateToItem(item: any): void {
    this.isExpanded = false;
    this.skipAutoExpand = true;
    setTimeout(() => { this.skipAutoExpand = false; }, 5000);
    const ch = item.channelName || this.channelName;
    setTimeout(() => {
      switch (item.badge) {
        case 'LIVE':
        case 'UPCOMING': {
          // Events always hang off the network slug from the URL: an event's channel_name can be
          // its group's own channel (e.g. "akron-athletics-2614"), and a network-level event must
          // not inherit whatever group the fan is currently browsing.
          const network = this.channelName || ch;
          const prefix = item.groupSlug ? `/${network}/${item.groupSlug}` : `/${network}`;
          // A ticket_key means the user already holds a ticket for this event - send them
          // straight to the stream (single-session events) or the schedule (multi-session),
          // instead of the generic event landing page.
          if (item.ticketKey) {
            if (item.selectedType === 'single_session_event') {
              this.router.navigate([`${prefix}/stream/${item.ticketKey}`], { queryParams: { stage: item.streamid } });
            } else {
              this.router.navigate([`${prefix}/event/schedule/${item.ticketKey}`]);
            }
          } else {
            this.router.navigate([`${prefix}/event/${item.slug}`]);
          }
          break;
        }
        case item.tag_category: {
          // Use this item's own group (it may belong to a preferred group different from
          // whatever group is currently active), not this.currentGroupSlug.
          const base = item.groupSlug
            ? `/${this.channelName}/${item.groupSlug}`
            : this.commanSubscribeService.getChannelBasePath(this.channelName);
          this.router.navigate([base + "/album/" + item.tag_category + "/" + item.slug]);
          break;
        }
        case 'MERCH DROP': {
          // AI_RECOMMENDED_FOR_YOU's products are individual items (product_id is always
          // present; collection_ids is frequently null) - route to the product detail page,
          // not the collection listing, matching the pattern used elsewhere (e.g.
          // global-pubnub.service.ts, product-marketplace.component.ts). Use this item's own
          // group (may differ from whatever group is currently active) so the URL comes out
          // as channelName/groupSlug/merchandise/... when the item belongs to one.
          const base = item.groupSlug
            ? `/${ch}/${item.groupSlug}`
            : this.commanSubscribeService.getChannelBasePath(ch);
          this.router.navigate([`${base}/merchandise/overviewProduct/${item.productId}/${item.collectionId ?? 0}`]);
          break;
        }
        case 'COMMUNITY': {
          const groupBase = item.groupSlug
            ? `/${ch}/${item.groupSlug}`
            : this.commanSubscribeService.getChannelBasePath(ch);
          this.router.navigate([`${groupBase}/community/${item.channelId || ''}`]);
          break;
        }
        default: break;
      }
    }, 200);
  }

  redirectToAlbum(data: any) {
    const base = data.slug
      ? `/${this.channelName}/${data.slug}`
      : this.commanSubscribeService.getChannelBasePath(this.channelName);
    this.router.navigate([base + "/album/" + data.tag_category + "/" + data.slug]);

  }

  // ── Chat history ────────────────────────────────────────────────────────────

  loadAllChatHistories(): void {
    if (!this.userInfo?.id) return;
    this.isLoadingHistory = true;
    const fd = new FormData();
    fd.append('user_id', this.userInfo.id);
    fd.append('organization_id', this.org_id);
    fd.append('status', 'active');
    // if (this.currentGroupId) fd.append('group_id', this.currentGroupId);

    this.appService.postBase1Request(`${API.AI_FAN_AGENT}/get_chats?NOLOADER`, fd).subscribe({
      next: (res: any) => {
        this.chatHistories = (res?.threads || []).map((t: any) => ({
          id: t.thread_id,
          dynamo_id: t.id,
          title: t.chat_title || 'New conversation',
          timestamp: t.last_timestamp,
          interactionCount: t.interaction_count,
          totalTokens: t.total_tokens,
          totalCost: t.total_cost,
          likedMessagesCount: t.liked_messages_index?.length || 0,
          dislikedMessagesCount: t.disliked_messages_index?.length || 0,
          message_count: t.interaction_count,
          channelName: this.channelName,
        }));
        this.isLoadingHistory = false;
        this.cdr.detectChanges();
      },
      error: () => { this.isLoadingHistory = false; }
    });
  }

  get visibleChatHistories(): any[] {
    return this.showAllChats ? this.chatHistories : this.chatHistories.slice(0, this.CHAT_PREVIEW_COUNT);
  }

  toggleShowAllChats(): void {
    this.showAllChats = !this.showAllChats;
  }

  loadChatHistory(chat: any): void {
    if (!this.userInfo?.id) return;
    this.isLoadingHistory = true;
    const fd = new FormData();
    fd.append('user_id', this.userInfo.id);
    fd.append('thread_id', chat.id);
    fd.append('agent_name', 'channel_fan_agent');
    fd.append('organization_id', this.org_id);
    fd.append('status', 'active');

    this.appService.postBase1Request(`${API.AI_FAN_AGENT}/get_chat?NOLOADER`, fd).subscribe({
      next: (res: any) => {
        if (res?.chat_history) {
          const allMessages = res.chat_history.conversation.flat();
          const greetingMsg = {
            message: this.channelDisplayName
              ? this.getchannelInitialMessage(this.channelDisplayName)
              : this.getGlobalInitialMessage(),
            time: allMessages[0] ? this.formatServerTime(allMessages[0].timestamp) : moment().format('hh:mm A'),
            sender: 'bot',
            isStreaming: false,
            hideActions: true,
          };
          const formatted = this.formatMessagesForDisplay(allMessages);
          const withFeedback = this.applyFeedbackToMessages(
            formatted,
            res.liked_messages_index || [],
            res.disliked_messages_index || [],
            res.message_feedback || {}
          );
          this.messagesList.next([greetingMsg, ...withFeedback]);
          this.currentChatId = chat.id;
          this.threadId = chat.id;
          this.isFirstBotResponse = false;
          this.scrollToBottom();
        }
        this.isLoadingHistory = false;
      },
      error: () => { this.isLoadingHistory = false; }
    });
  }

  formatMessagesForDisplay(apiMessages: any[]): any[] {
    return apiMessages.map((msg, idx) => ({
      message: msg.role === 'HumanMessage' ? msg.content : this.parseMarkdown(msg.content),
      time: this.formatServerTime(msg.timestamp),
      sender: msg.role === 'HumanMessage' ? 'user' : 'bot',
      isStreaming: false,
      messageIndex: idx,
      feedback: null,
    }));
  }

  // get_chat timestamps are UTC without an offset; plain moment() would read them as local time.
  formatServerTime(ts: any): string {
    return moment.utc(ts).local().format('hh:mm A');
  }

  applyFeedbackToMessages(
    messages: any[],
    likedIndexes: number[] = [],
    dislikedIndexes: number[] = [],
    feedbackMap: Record<string, string> = {}
  ): any[] {
    const likedSet = new Set<number>(likedIndexes);
    const dislikedSet = new Set<number>(dislikedIndexes);
    return messages.map(msg => {
      if (msg.sender !== 'bot') return { ...msg, feedback: null };
      const idx = msg.messageIndex;
      if (idx == null) return { ...msg, feedback: null };
      if (feedbackMap[idx] !== undefined) return { ...msg, feedback: feedbackMap[idx] };
      if (likedSet.has(idx)) return { ...msg, feedback: 'like' };
      if (dislikedSet.has(idx)) return { ...msg, feedback: 'dislike' };
      return { ...msg, feedback: null };
    });
  }

  private parseMarkdown(text: string): string {
    if (!text) return text;
    return text
      .replace(/\*\*([\s\S]+?)\*\*/g, '<b>$1</b>')
      .replace(/(?<!\*)\*(?!\*)([^\n*]+?)\*(?!\*)/g, '<i>$1</i>');
  }

  // Network/org-level fallback, used when the community item has no group of its own to
  // show a logo for (see getCommunityLogo for the per-item group_logo/group_base_logo check).
  getCommunityCardImage(): string {
    if (this.savedGroupNetworkIds?.length > 0 && this.groupsData?.length) {
      const preferredGroup = this.groupsData.find((g: any) =>
        this.savedGroupNetworkIds.includes(Number(g.group_id || g.id))
      );
      if (preferredGroup?.group_base_logo || preferredGroup?.group_logo) {
        return preferredGroup.group_base_logo || preferredGroup.group_logo;
      }
    }
    return this.getNavLogo();
  }

  // Follows the route only (org_info.channel_logo is swapped to the group's logo on group routes),
  // never the fan's saved group preference.
  getNavLogo(): string {
    if (this.settingsData?.channelInfo?.channel_id == 0)
      return this.org_info?.channel_logo || this.defaultImage;
    if (this.settingsData?.channelInfo?.channel_logo)
      return this.settingsData.channelInfo.channel_logo;
    if (this.org_info?.channel_logo)
      return this.org_info.channel_logo;
    return this.org_info?.organization_logo || this.defaultImage;
  }

  // Priority: the community item's own group (if it has a network_group_id) - its own
  // group_logo field first, else the matching group's group_logo/group_base_logo from
  // groupsData - and only then the generic network/org-level fallback.
  private getCommunityLogo(item: any): string {
    return this.getItemGroupLogo(item) || this.getCommunityCardImage();
  }

  // The item's own group logo ('' when it has no group or the group has no logo).
  private getItemGroupLogo(item: any): string {
    if (!item.network_group_id) return '';
    const group = this.groupsData?.find((g: any) =>
      Number(g.group_id || g.id) === Number(item.network_group_id)
    );
    return item.group_logo || group?.group_logo || group?.group_base_logo || '';
  }

  // A video without a thumbnail shows the logo of the group it belongs to, else the network's.
  getVodFallbackImage(item: any): string {
    return this.getItemGroupLogo(item) || this.org_info?.channel_logo || this.org_info?.organization_logo || this.defaultImage;
  }
  // item.group_slug can be stale vs the network's current group slug (which
  // ChannelWebpageGuard validates against, else 404), so the id lookup wins.
  private getGroupSlugForItem(item: any): string {
    if (item.network_group_id) {
      const group = this.groupsData?.find((g: any) =>
        Number(g.group_id || g.id) === Number(item.network_group_id)
      );
      if (group?.group_slug) return group.group_slug;
    }
    if (!item.group_slug || !this.groupsData?.length) return item.group_slug || '';
    return this.groupsData.some((g: any) => g.group_slug === item.group_slug) ? item.group_slug : '';
  }

  private hexToRgb(color: string): { r: number; g: number; b: number } | null {
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color || '');
    if (!match) return null;
    return { r: parseInt(match[1], 16), g: parseInt(match[2], 16), b: parseInt(match[3], 16) };
  }

  // 0 (black) to 1 (white) relative luminance, used to auto-pick a readable text color
  // and to decide which gradient treatment (glossy vs glass) a button needs.
  private getLuminance(color: string): number {
    const rgb = this.hexToRgb(color);
    if (!rgb) return 1;
    return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  }

  private isDarkColor(color: string): boolean {
    return this.getLuminance(color) < 0.5;
  }

  // Mixes a color towards black by `factor` (0-1). Optional alpha for a translucent stop.
  private darkenColor(r: number, g: number, b: number, factor: number, alpha = 1): string {
    return `rgba(${Math.round(r * factor)}, ${Math.round(g * factor)}, ${Math.round(b * factor)}, ${alpha})`;
  }

  // Looks up the current theme background color for a recommendation card at render
  // time (event/merchandise/community/vod), so it's never stuck with a stale value from
  // whenever settingsData happened to load.
  getItemBadgeColor(item: any): string {
    switch (item?.category) {
      case 'event': return this.settingsData?.event_button_bg_primary_color || (item.badge === 'LIVE' ? BADGE_COLORS['LIVE'] : BADGE_COLORS['UPCOMING']);
      case 'merchandise': return this.settingsData?.merchandise_button_bg_primary_color || BADGE_COLORS['MERCH DROP'];
      case 'community': return this.settingsData?.space_button_primary_color || BADGE_COLORS['COMMUNITY'];
      default: return this.settingsData?.bg_primary_color || BADGE_COLORS[item?.tag_category] || BADGE_COLORS['VOD'];
    }
  }

  // Same live lookup for the paired text color - but if the configured font color ends up
  // too close in brightness to the card's own background color (bad theme data, or a
  // fallback collision), auto-flips to black/white instead of rendering unreadable text.
  getItemBadgeFontColor(item: any): string {
    let font: string;
    switch (item?.category) {
      case 'event': font = this.settingsData?.event_button_font_primary_color || '#ffffff'; break;
      case 'merchandise': font = this.settingsData?.merchandise_button_font_primary_color || '#ffffff'; break;
      case 'community': font = this.settingsData?.space_button_font_primary_color || '#ffffff'; break;
      default: font = this.settingsData?.font_primary_color || '#ffffff';
    }
    const bgLuminance = this.getLuminance(this.getItemBadgeColor(item));
    const fontLuminance = this.getLuminance(font);
    if (Math.abs(bgLuminance - fontLuminance) < 0.35) {
      return bgLuminance < 0.5 ? '#ffffff' : '#000000';
    }
    return font;
  }

  // CTA button text: same rule as the badge/pill font color - default to that category's
  // configured theme font color, falling back to black/white only when it wouldn't be
  // legible against the item's theme background color.
  getCtaTextColor(item: any): string {
    return this.getItemBadgeFontColor(item);
  }

  // Builds the recommendation-card CTA gradient from the card's badge/border color
  // (event, content, merchandise, community). The theme color stays strong through the
  // top (so the button reads as that color) and genuinely fades into black toward the
  // bottom - a real two-tone color+black blend - which is what lets the theme-colored
  // text from getCtaTextColor stay legible (bright color on a dark backdrop).
  getGradient(color: string): string {
    const rgb = this.hexToRgb(color) || this.hexToRgb(this.accentColor);
    if (!rgb) {
      return 'linear-gradient(180deg, rgba(235, 169, 33, 0.28) 0%, rgba(82, 55, 20, 0.95) 18%, #2a1c12 55%, #15100d 100%)';
    }
    const { r, g, b } = rgb;
    const midShade = this.darkenColor(r, g, b, 0.28);
    const blackShade = this.darkenColor(r, g, b, 0.12);
    return `linear-gradient(180deg, rgba(${r}, ${g}, ${b}, 0.85) 0%, rgba(${r}, ${g}, ${b}, 0.4) 0%, ${midShade} 65%, ${blackShade} 100%)`;
  }

  // Dark (navy) text-fallback buttons get a plain lift shadow; theme-colored text gets a
  // soft glow in its own color, matching the button's outer halo.
  getButtonTextShadow(item: any): string {
    const bg = this.getItemBadgeColor(item);
    if (this.isDarkColor(bg)) {
      return '0 1px 2px rgba(0, 0, 0, 0.4)';
    }
    const rgb = this.hexToRgb(bg);
    if (!rgb) return 'none';
    return `0 0 6px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.6)`;
  }

  // Builds the recommendation-card CTA glow (box-shadow) from the same badge/border color
  // so it never looks mismatched against a themed border/gradient - a two-layer outer
  // halo (tight + soft) for the glowing-button look, plus a subtle inset highlight.
  getGlow(color: string): string {
    const rgb = this.hexToRgb(color) || this.hexToRgb(this.accentColor);
    if (!rgb) {
      return 'inset 0 1px 0 rgba(255, 235, 180, 0.45), inset 0 0 10px rgba(235, 169, 33, 0.22), 0 0 10px rgba(235, 169, 33, 0.5), 0 0 20px rgba(235, 169, 33, 0.25)';
    }
    const { r, g, b } = rgb;
    return `inset 0 1px 0 rgba(255, 255, 255, 0.25), inset 0 0 10px rgba(${r}, ${g}, ${b}, 0.35), 0 0 10px rgba(${r}, ${g}, ${b}, 0.5), 0 0 20px rgba(${r}, ${g}, ${b}, 0.25)`;
  }

  // Faded border for the unselected profile-preference chips - keeps the original
  // design's low-opacity look while still tracking the theme's text color instead of a
  // hardcoded white.
  getFadedColor(color: string, opacity: number): string {
    const rgb = this.hexToRgb(color);
    if (!rgb) {
      return `rgba(255, 255, 255, ${opacity})`;
    }
    const { r, g, b } = rgb;
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  updateMessageFeedback(message: any, feedback: 'like' | 'dislike'): void {
    if (!this.userInfo?.id || !this.currentChatId || message.sender !== 'bot') return;
    if (message.feedback === feedback) return;

    let messageIndex = message.messageIndex;
    if (messageIndex == null) {
      const conversationMessages = this.messagesList.getValue().filter((m: any) => !m.hideActions);
      messageIndex = conversationMessages.findIndex((m: any) => m === message);
    }

    const currentMessages = this.messagesList.getValue();
    this.messagesList.next(currentMessages.map((m: any) => m === message ? { ...m, feedback, feedbackInteracted: true } : m));

    const payload: any = {
      user_id: this.userInfo.id,
      thread_id: this.currentChatId,
      organization_id: this.org_id,
      agent_name: 'channel_fan_agent',
      message_index: messageIndex,
      feedback: feedback
    };
    if (this.currentGroupId) payload['group_id'] = this.currentGroupId;

    this.appService.postBase1Request(`${API.AI_FAN_AGENT_MESSAGE_FEEDBACK}?NOLOADER`, payload).subscribe({
      error: () => this.messagesList.next(currentMessages)
    });
  }

  deleteChatHistory(chatId: string, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '370px',
      panelClass: 'app-confirmation-dialog',
      data: { title: 'Are you sure you want to delete this chat?', settingsData: this.settingsData || null }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result !== 'yes') return;
      const fd = new FormData();
      fd.append('id', chatId);
      this.appService.postBase1Request(`${API.AI_FAN_AGENT}/delete_chat?NOLOADER`, fd).subscribe({
        next: (res: any) => {
          if (res?.status === 'success') {
            this.chatHistories = this.chatHistories.filter(h => h.dynamo_id !== chatId);
            if (this.currentChatId === chatId) this.createNewChat();
            this.toastr.success('Chat deleted successfully');
          }
        },
        error: () => this.toastr.error('Failed to delete chat')
      });
    });
  }

  deleteAllChatHistories(event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '370px',
      panelClass: 'app-confirmation-dialog',
      data: { title: 'Are you sure you want to delete all chats?', settingsData: this.settingsData || null }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result !== 'yes') return;
      const fd = new FormData();
      fd.append('user_id', this.userInfo.id);
      fd.append('organization_id', this.org_id);
      if (this.currentGroupId) fd.append('group_id', this.currentGroupId);
      this.appService.postBase1Request(`${API.AI_FAN_AGENT}/delete_chats?NOLOADER`, fd).subscribe({
        next: (res: any) => {
          if (res?.status === 'success') {
            this.chatHistories = [];
            this.createNewChat();
            this.toastr.success('All chats deleted successfully');
          }
        },
        error: () => this.toastr.error('Failed to delete all chats')
      });
    });
  }

  createNewChat(): void {
    this.threadId = null;
    this.currentChatId = null;
    this.messagesList.next([]);
    this.userInput = '';
    this.currentStreamingMessage = null;
    this.isMessageStreaming = false;
    this.hasReplyStarted = false;
    this.groupTypeDisplayed = false;
    this.personalizeMessageDisplayed = false;
    this.preferencesCheckInFlight = false;
    this.groupingQuestions = [];
    this.initialMessageSent = false;
    this.isFirstBotResponse = true;
    this.streamingSubscription?.unsubscribe();
    this.streamingSubscription = null;

    if (this.userInfo?.id && this.org_id) {
      if (!this.currentGroupId) {
        this.fetchGroupingQuestions();
        this.checkAgentPreference();
      }
      this.checkChannelAgentPreference();
    }
    // ADD THIS
    this.sendInitialGreeting();

    this.scrollToTop();
  }

  toggleChat(): void {
    this.isExpanded = !this.isExpanded;
    if (this.isExpanded) {
      this.sendInitialGreeting();
      setTimeout(() => { this.scrollToBottom(); }, 100);
    } else {
      this.preferencesDialogRef?.close();
      // Minimizing mid-edit should discard the unsaved edit, same as a page refresh would.
      if (this.isEditingGroupProfile) this.onCancelEditGroupProfile();
      if (this.isEditingChannelProfile) this.onCancelEditChannelProfile();
    }
  }

  // ── Send message ────────────────────────────────────────────────────────────

  sendMessage(): void {
    if ((!this.userInput.trim() && this.selectedFiles.length === 0) || this.isLoading || this.isMessageStreaming || this.chatPaused) return;

    // Wait for org info if channel is set but API hasn't resolved yet
    if (this.channelName && !this.orgInfoLoaded) {
      setTimeout(() => this.sendMessage(), 200);
      return;
    }

    this.lastUserInput = this.userInput;
    const input = this.userInput;
    const filesCopy: any[] = [...this.selectedFiles];
    const filesSnapshot = filesCopy.map((f: any) => ({ url: f.previewUrl, type: f.type, name: f.name }));

    const userMsg: any = { message: input, time: moment().format('hh:mm A'), sender: 'user' };
    if (filesSnapshot.length > 0) userMsg.files = filesSnapshot;

    this.messagesList.next([...this.messagesList.getValue(), userMsg]);
    this.userInput = '';
    this.selectedFiles = [];
    setTimeout(() => {
      if (this.messageInput?.nativeElement) {
        this.messageInput.nativeElement.style.height = 'auto';
      }
    }, 0);

    const fd = new FormData();
    fd.append('user_input', input);
    fd.append('thread_id', this.threadId || '');
    fd.append('timezone', this.userInfo?.timezone || 'Asia/Kolkata');
    fd.append('channel_name', this.channelName);
    fd.append('organization_id', this.org_id);
    fd.append('business_name', this.businessName);
    fd.append('channel_display_name', this.channelDisplayName);
    // Dynamic Agent Name (specs/dynamic-agent-name): named agent_display_name,
    // NOT agent_name — this same endpoint already uses agent_name internally
    // as a supervisor routing label ("channel_fan_agent"), see loadChatHistory()
    // above. Only sent when set; the LLM's own greeting falls back to the
    // existing "{channel} Fan Agent" wording when empty.
    if (this.displayAgentName) {
      fd.append('agent_display_name', this.displayAgentName);
    }
    fd.append('performer_id', this.performerId || '');
    fd.append('channel_id', this.channelId);
    if (this.currentGroupId) {
      fd.append('group_id', this.currentGroupId);
      fd.append('group_name', this.currentGroupName);
    }
    if (this.selectedChannelData?.length) {
      this.selectedChannelData.forEach((ch: any) => {
        fd.append('preferred_channels_id', ch.channel_id);
        fd.append('preferred_channels', ch.channel_name);
        fd.append('preferred_channels_display_names', ch.channel_display_name);
      });
      this.selectedChannelData = [];
    }
    // Attach selected files
    filesCopy.forEach((f: any) => {
      if (f?.file) fd.append('files', f.file);
    });

    this.getAIAgentResponse(fd);
    setTimeout(() => this.scrollToBottom(), 100);
  }

  getAIAgentResponse(formData: FormData): void {
    this.startLoadingAnimation();
    const url = API.AI_FAN_AGENT_CHANNEL;
    const username = 'fan_' + this.userInfo.id;
    const userInfoStored = JSON.parse(localStorage.getItem('user_Info') || '{}');
    const accessToken = userInfoStored.access_token;

    this.currentStreamingMessage = {
      message: '',
      time: moment().format('hh:mm A'),
      sender: 'bot',
      isStreaming: true,
    };
    this.messagesList.next([...this.messagesList.getValue(), this.currentStreamingMessage]);

    this.streamingSubscription = this.appService.postAgentStreamFetch(url, formData, username, accessToken).subscribe({
      next: (chunk: any) => {
        if (chunk.action && chunk.message) {
          this.currentStreamingMessage.message = chunk.message;
          this.currentStreamingMessage.isStreaming = true;
          this.isMessageStreaming = true;
          this.hasReplyStarted = false;
          this.isFirstBotResponse = false;
          this.throttledScroll();
          return;
        }

        if (chunk.response_out_delta) {
          this.stopLoadingAnimation();
          if (!this.hasReplyStarted) {
            this.currentStreamingMessage.message = '';
            this.hasReplyStarted = true;
          }
          this.currentStreamingMessage.isStreaming = false;
          this.isMessageStreaming = true;
          this.currentStreamingMessage.message += chunk.response_out_delta;
          this.messagesList.next([...this.messagesList.getValue()]);
          this.throttledScroll();
        } else if (chunk.response_out) {
          this.stopLoadingAnimation();
          this.isMessageStreaming = false;
          this.hasReplyStarted = false;
          this.currentStreamingMessage.message = this.parseMarkdown(chunk.response_out);
          this.currentStreamingMessage.isStreaming = false;
          if (isPausedReply(chunk)) { this.chatPaused = true; }
          this.isFirstBotResponse = false;
          this.cdr.detectChanges();
          if (chunk.thread_id && !this.threadId) {
            this.threadId = chunk.thread_id;
            this.currentChatId = chunk.thread_id;
            this.loadAllChatHistories();
          }
          setTimeout(() => this.scrollToBottom(), 100);
        }
        this.isFirstBotResponse = false;
      },
      error: () => {
        if (this.currentStreamingMessage) {
          this.currentStreamingMessage.message =
            '<strong>This request is taking a little longer than expected.</strong><br />' +
            '<strong>We\'re retrying automatically.</strong> Please hang tight.';
          this.currentStreamingMessage.isStreaming = false;
          setTimeout(() => {
            this.userInput = this.lastUserInput;
            this.sendMessage();
          }, 1000);
        }
        this.stopLoadingAnimation();
        this.isMessageStreaming = false;
        this.isFirstBotResponse = false;
      }
    });
  }

  // ── Loading animation ───────────────────────────────────────────────────────

  startLoadingAnimation(): void {
    this.isLoading = true;
    this.intervalId = setInterval(() => {
      this.loadingStep = (this.loadingStep + 1) % 4;
    }, 300);
  }

  stopLoadingAnimation(): void {
    this.isLoading = false;
    clearInterval(this.intervalId);
  }

  // ── Scroll ──────────────────────────────────────────────────────────────────

  onScroll(event: any): void {
    if (this.isExpandingTable) return;
    const el = event.target;
    const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    this.showScrollToBottomArrow = distanceFromBottom > this.scrollThreshold;
  }

  throttledScroll(): void {
    const now = Date.now();
    if (now - this.lastScrollTime > this.scrollThrottleDelay) {
      this.lastScrollTime = now;
      this.scrollToBottomImmediate();
    }
  }

  scrollToBottomImmediate(): void {
    if (this.messagesContainer?.nativeElement) {
      const el = this.messagesContainer.nativeElement;
      el.scrollTop = el.scrollHeight - el.clientHeight;
    }
  }

  // ── Table expansion ─────────────────────────────────────────────────────────

  shouldTruncateMessage(message: string): boolean {
    return hiddenTableRowCount(message, this.MAX_TABLE_ROWS) > 0;
  }

  getTruncatedMessage(message: string): string {
    return collapseTableRows(message, this.MAX_TABLE_ROWS);
  }

  getRemainingRows(message: string): number {
    return hiddenTableRowCount(message, this.MAX_TABLE_ROWS);
  }

  trackByIndex(index: number): number { return index; }

  getMessageId(msg: any): string {
    return `${msg.time}_${msg.sender}_${(msg.message || '').substring(0, 50)}`;
  }

  isMessageExpanded(msg: any): boolean {
    return this.expandedMessages.has(this.getMessageId(msg));
  }

  toggleMessageExpansion(msg: any): void {
    this.isExpandingTable = true;
    const id = this.getMessageId(msg);
    if (this.expandedMessages.has(id)) {
      this.expandedMessages.delete(id);
    } else {
      this.expandedMessages.add(id);
    }
    this.cdr.detectChanges();
    setTimeout(() => {
      this.scrollToBottomImmediate();
      this.isExpandingTable = false;
    }, 100);
  }

  // ── Input helpers ────────────────────────────────────────────────────────────

  adjustTextareaHeight(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  useSuggestion(s: string): void { this.userInput = s; this.sendMessage(); }

  onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey && !this.isLoading && !this.isMessageStreaming) {
      e.preventDefault();
      this.sendMessage();
    }
  }

  navigate(route: string): void { this.isExpanded = false; this.router.navigateByUrl(route); }

  // Groups row (dashboard, above "Recommended for You").
  get showGroupsRow(): boolean {
    return !!this.isGroupSupported && !!this.groupsData?.length;
  }

  isActiveGroup(group: any): boolean {
    return !!group?.group_slug && group.group_slug === this.currentGroupSlug;
  }

  // Navigating updates the URL only. The existing router.events NavigationEnd subscription
  // above already re-derives currentGroupId/currentGroupSlug/currentGroupName from the new URL
  // and reloads the dashboard/chat for the new group context, matching ic-home.component.ts's
  // goToGroup(). Unlike navigate(), this intentionally does not collapse the agent panel -
  // switching groups should keep the dashboard open.
  selectGroup(group: any): void {
    if (!group?.group_slug || this.isActiveGroup(group)) return;
    this.router.navigateByUrl('/' + this.channelName + '/' + group.group_slug);
  }

  // "All" chip - clears the group filter back to the network-wide/channel context. Same
  // NavigationEnd-driven refresh as selectGroup() (no navMatchedGroup for a bare channel URL).
  viewAllGroups(): void {
    if (!this.currentGroupSlug) return;
    this.router.navigateByUrl('/' + this.channelName);
  }

  // Top bar menu, member bar and groups dialog: a group, or null for the whole network.
  onScopeSelected(group: any | null): void {
    if (group) this.selectGroup(group);
    else this.viewAllGroups();
  }

  openGroupsDialog(): void {
    this.dialog.open(AgentGroupsDialogComponent, {
      data: { networkName: this.networkName, groupsLabel: this.groupsLabel, groups: this.sortedGroups, currentGroupSlug: this.currentGroupSlug },
      width: '560px',
      maxWidth: '95vw',
      panelClass: 'agent-home-dialog-panel',
    }).afterClosed().subscribe((picked: any) => {
      if (picked !== undefined) this.onScopeSelected(picked);
    });
  }

  // Marketplace Events page, scoped to the selected group when there is one.
  browseAllEvents(): void {
    const group = this.currentGroupSlug ? '/' + this.currentGroupSlug : '';
    this.navigate('/' + this.channelName + group + '/merchandise/event');
  }

  // Preferences UI (#preferencesTpl) in a dialog. Opens straight into edit mode when preferences
  // already exist; closing without saving discards the edit, and a successful save closes it.
  openPreferencesDialog(): void {
    if (!this.userInfo?.id) { this.openSignIn(); return; }
    if (this.preferencesDialogRef) return;
    if (this.anyPreferencesSaved && !this.isEditingGroupProfile && !this.isEditingChannelProfile) this.onEditAllPreferences();
    this.preferencesDialogRef = this.dialog.open(this.preferencesDialogTpl, {
      width: '640px',
      maxWidth: '95vw',
      maxHeight: '86vh',
      panelClass: 'agent-home-dialog-panel',
    });
    this.preferencesDialogRef.afterClosed().subscribe(() => {
      this.preferencesDialogRef = null;
      if (this.isEditingGroupProfile || this.isEditingChannelProfile) this.onCancelEditAllPreferences();
    });
  }

  closePreferencesDialog(): void {
    this.preferencesDialogRef?.close();
  }

  // Chat history pop-up (#chatHistoryDialogTpl, v2 right-panel card "Show"; US-18). Rendered from the
  // template so the list stays bound to chatHistories while chats are deleted.
  openChatHistoryDialog(): void {
    if (this.chatHistoryDialogRef) return;
    this.chatHistoryDialogRef = this.dialog.open(this.chatHistoryDialogTpl, {
      width: '560px',
      maxWidth: '95vw',
      maxHeight: '86vh',
      panelClass: 'agent-home-dialog-panel',
    });
    this.chatHistoryDialogRef.afterClosed().subscribe(() => { this.chatHistoryDialogRef = null; });
  }

  closeChatHistoryDialog(): void {
    this.chatHistoryDialogRef?.close();
  }

  formatTime(ts: any): string { return ts ? moment(ts).fromNow() : ''; }

  truncateLabel(text: string, maxLen = 22): string {
    if (!text) return '';
    return text.length > maxLen ? text.substring(0, maxLen) + '…' : text;
  }

  scrollToBottom(): void {
    setTimeout(() => {
      if (this.messagesContainer?.nativeElement) {
        this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
      }
      this.showScrollToBottomArrow = false;
    }, 100);
  }

  private scrollToTop(): void {
    setTimeout(() => {
      if (this.messagesContainer?.nativeElement) {
        this.messagesContainer.nativeElement.scrollTop = 0;
      }
    }, 50);
  }

  // ── File attachments ─────────────────────────────────────────────────────────

  getFileIcon(type: string): string {
    switch (type) {
      case 'video': return 'videocam';
      case 'audio': return 'audiotrack';
      case 'document': return 'description';
      default: return 'insert_drive_file';
    }
  }

  getFileCategory(file: File): string {
    const mimeType = file.type;
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    return 'document';
  }

  getFileExtension(name: string): string {
    return name.split('.').pop() || '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const filesArray = Array.from(input.files);
      this.selectedFiles = filesArray.map(file => ({
        file,
        type: this.getFileCategory(file),
        name: file.name,
        previewUrl: URL.createObjectURL(file),
      }));
    }
    // Reset input so the same file can be selected again
    (event.target as HTMLInputElement).value = '';
  }

  removeFile(index: number): void {
    const file = this.selectedFiles[index];
    if (file?.previewUrl) {
      this.selectedFiles.splice(index, 1);
    }
  }

  downloadFile(file: any): void {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // ── Voice recording ──────────────────────────────────────────────────────────

  toggleAudioRecording(): void {
    if (this.isRecording) {
      this.confirmAndSend();
    } else {
      this.startRecording();
    }
  }

  startRecording(): void {
    this.isCancelled = false;
    this.audioChunks = [];
    this.isRecording = true;
    this.cdr.detectChanges();
    try {
      const waveformContainer = document.querySelector('#fa-waveform-live');
      if (!waveformContainer) throw new Error('Waveform container not found');

      this.waveSurferLive = WaveSurfer.create({
        container: '#fa-waveform-live',
        waveColor: '#eba921',
        progressColor: '#eba921',
        height: 28,
        barWidth: 1,
        barGap: 2,
        barHeight: 1,
        barRadius: 0,
        cursorWidth: 0,
        interact: false,
        normalize: true,
        responsive: true,
        plugins: [
          MicrophonePlugin.create({ constraints: { audio: true, video: false } })
        ]
      } as any);

      const micPlugin = (this.waveSurferLive as any).microphone;
      if (!micPlugin) { console.error('Microphone plugin unavailable'); return; }

      micPlugin.on('deviceReady', (micStream: MediaStream) => {
        this.mediaRecorder = new MediaRecorder(micStream);
        this.audioChunks = [];

        this.mediaRecorder.ondataavailable = (event) => {
          this.audioChunks.push(event.data);
        };

        this.mediaRecorder.onstop = () => {
          if (!this.isCancelled) {
            this.audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
            this.audioUrl = URL.createObjectURL(this.audioBlob);
            if (this.pendingSend) {
              this.pendingSend = false;
              this.isRecording = false;
              this.isProcessing = true;
              this.cdr.detectChanges();
              this.transcribeAudio(this.audioBlob);
            } else {
              this.isRecording = false;
              this.cdr.detectChanges();
              this.loadFrozenWaveform(this.audioBlob);
            }
          } else {
            this.audioBlob = null;
            this.audioUrl = null;
            this.isCancelled = false;
            this.isRecording = false;
            this.cdr.detectChanges();
          }
        };

        this.mediaRecorder.start();
      });

      micPlugin.on('deviceError', (err: any) => {
        console.error('Microphone error:', err);
        this.cancelRecording();
      });

      micPlugin.start();
    } catch (err) {
      console.error('Mic permission error', err);
      this.isRecording = false;
    }
  }

  cancelRecording(): void {
    this.isCancelled = true;
    this.pendingSend = false;
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    this.waveSurferLive?.destroy();
    this.waveSurferLive = null;
    this.isRecording = false;
    this.resetAudio();
    this.cdr.detectChanges();
  }

  confirmAndSend(): void {
    this.waveSurferLive?.destroy();
    this.waveSurferLive = null;
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.pendingSend = true;
      this.mediaRecorder.stop();
    } else if (this.audioBlob) {
      this.transcribeAudio(this.audioBlob);
    }
  }

  private transcribeAudio(blob: Blob): void {
    this.isProcessing = true;
    const formData = new FormData();
    formData.append('audio', blob, 'voice-message.webm');
    formData.append('organization_id', this.org_id);
    formData.append('user_id', this.userInfo?.id || 'fanuser');
    formData.append('user_type', 'fan');
    formData.append('username', this.userInfo?.user_display_name || 'fanuser');

    this.appService.postBaseRequest(`${API.AI_TRANSCRIBE}?NOLOADER`, formData).subscribe({
      next: (result: any) => {
        if (result.statusCode === 200) {
          this.userInput = result.transcription;
        }
        this.resetAudio();
        this.isProcessing = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.resetAudio();
        this.isProcessing = false;
        this.cdr.detectChanges();
      }
    });
  }

  loadFrozenWaveform(blob: Blob): void {
    setTimeout(() => {
      this.waveSurferFrozen = WaveSurfer.create({
        container: '#fa-waveform-frozen',
        waveColor: '#eba921',
        progressColor: '#eba921',
        height: 28,
        barWidth: 1,
        barGap: 2,
        cursorWidth: 0,
        interact: false,
        responsive: true,
        normalize: true,
        minPxPerSec: 50,
      } as any);
      this.waveSurferFrozen.loadBlob(blob);
      this.waveSurferFrozen.on('ready', () => {
        this.waveSurferFrozen?.seekTo(0.95);
      });
    }, 100);
  }

  resetAudio(): void {
    this.audioChunks = [];
    this.audioBlob = null;
    this.audioUrl = null;
    (this.mediaRecorder as any) = null;
    this.waveSurferLive?.destroy();
    this.waveSurferLive = null;
    this.waveSurferFrozen?.destroy();
    this.waveSurferFrozen = null;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.streamingSubscription?.unsubscribe();
    // Prevents a pending debounced merch-tag refresh from firing against a destroyed component
    // (unnecessary API call + detectChanges on a destroyed view) if the fan navigates away within
    // the 300ms window after toggling a group. clearTimeout is always safe to call even if the
    // timer already fired or was never set (null) - it's a no-op in both cases, per spec.
    clearTimeout(this.merchTagRefreshTimer);
    clearInterval(this.countdownInterval);
    this.stopAutoScroll();
    this.stopLoadingAnimation();
    this.waveSurferLive?.destroy();
    this.waveSurferFrozen?.destroy();
    document.removeEventListener('click', this.handleGlobalClick.bind(this));
  }
}